<template>
  <div>
    <cbs-record-form
      v-if="record"
      :object="objectFull"
      :fields="srcRecord.record.columns"
      :record="record.record.record"
      :compare="srcRecord.record.record"
      mode="sid"
      hide
    />

    <!-- CUB-1288: контекст выбранного параметра формы — поля его записи (form_param.param_info), актуальность расчёта -->
    <div
      v-if="paramInfo.length || paramStale !== null || paramCalcError"
      class="pivot-param-info small mb-50"
    >
      <span
        v-for="(p, ix) in paramInfo"
        :key="ix"
        class="mr-2 text-nowrap"
      >
        <span class="text-muted">{{ p.title }}:</span> <b>{{ p.value }}</b>
      </span>
      <b-badge
        v-if="paramStale !== null"
        :variant="paramStale ? 'warning' : 'light-success'"
        class="mr-1"
      >
        {{ paramStale ? 'после сборки менялись данные' : 'расчёт актуален' }}
      </b-badge>
      <b-badge
        v-if="paramCalcError"
        variant="danger"
        :title="paramCalcError"
      >
        ошибка расчёта
      </b-badge>
    </div>

    <!-- new line button -->
    <div
      v-if="selectedCell && plusButtonPosition"
      class="floating-btn floating-plus-button"
      :style="{ top: plusButtonPosition.top + 'px', left: plusButtonPosition.left + 'px' }"
      @mousedown.stop
      @click="newLine()"
    >
      <feather-icon
        icon="PlusIcon"
        size="14"
        class="cursor-pointer"
      />
    </div>
    <!-- copy line button -->
    <div
      v-if="selectedCell && copyButtonPosition && showCopyButton"
      class="floating-btn floating-copy-button"
      :style="{ top: copyButtonPosition.top + 'px', left: copyButtonPosition.left + 'px' }"
      @mousedown.stop
      @click="copyLine()"
    >
      <feather-icon
        icon="CopyIcon"
        size="14"
        class="cursor-pointer"
      />
    </div>
    <!-- delete line button -->
    <div
      v-if="selectedCell && deleteButtonPosition && showDeleteButton"
      class="floating-btn floating-delete-button"
      :style="{ top: deleteButtonPosition.top + 'px', left: deleteButtonPosition.left + 'px' }"
      @mousedown.stop
      @click="deleteLine()"
    >
      <feather-icon
        icon="TrashIcon"
        size="14"
        class="cursor-pointer"
      />
    </div>

    <!-- BUG-782: несохранённые строки видны, и их можно сохранить явно, а не только уходом со строки -->
    <div
      v-if="isFormParamReady && dirtyCount() > 0"
      class="pivot-dirty-bar"
    >
      <span>Не сохранено строк: {{ dirtyCount() }}</span>
      <b-button
        size="sm"
        variant="warning"
        class="ml-1"
        :disabled="savingDirty"
        @mousedown.stop
        @click="saveDirtyRows()"
      >
        Сохранить
      </b-button>
    </div>

    <!-- table -->
    <div
      v-if="isFormParamReady && isRecordValid() && tableStatus === 'ready'"
      class="tablespace pivot-wrapper"
    >
      <table class="table table-bordered table-sm pivot-table enter-goes-right next-cell-no-activate">
        <thead style="z-index: 100;">
          <tr class="progress-row">
            <td
              :colspan="100"
              class="p-0 border-0"
            >
              <div
                class="progress"
                style="height: 6px; margin: 0; border-radius: 0;"
              >
                <div
                  v-if="loading > 0"
                  class="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                  role="progressbar"
                  style="width: 100%"
                  aria-label="Loading data"
                />
              </div>
            </td>
          </tr>
          <tr>
            <th
              :rowspan="rowSpanStyle()"
              style="vertical-align: middle;"
            >
              #
            </th>
            <th
              v-for="(h, hx) in formParam.column_heads"
              :key="`head_${hx}`"
              :rowspan="rowSpanStyle()"
              style="vertical-align: middle;"
            >
              {{ h }}
            </th>
            <th
              v-if="formParam.grand_total && formParam.value_cell"
              :colspan="valueEntities.length"
              style="vertical-align: middle;"
            >
              ВСЕГО
            </th>
            <th
              v-for="(col, cx) in getColumns()"
              :key="`col_${cx}`"
              :colspan="valueEntities.length"
              :class="[{ 'th-highlight': isHeaderSelected(cx, -1) }, `pt-${col.level_with_folders}`]"
              @click="onClickRowExpand(col, 'col')"
            >
              <feather-icon
                v-if="col.level < maxColumnLevel()"
                style="cursor: pointer;"
                :icon="col.isExpanded ? 'MinusSquareIcon' : 'PlusSquareIcon'"
              />
              {{ formatColumn(col) }}
            </th>
          </tr>
          <tr v-if="formParam.value_cell && formParam.value_cell.results.length > 1">
            <!--<th></th>-->
            <!--<th v-for="h in formParam.column_heads" :key="h"></th>-->
            <template v-if="formParam.grand_total">
              <th
                v-for="(res, idx) in valueEntities"
                :key="`res_${idx}`"
                :class="{ 'th-highlight': isHeaderSelected(0, idx) }"
                style="text-transform: none; font-weight: normal;"
              >{{ res.entity.name }}
              </th>
            </template>
            <template v-for="(col, ii) in getColumns()">
              <th
                v-for="(res, idx) in valueEntities"
                :key="`res_${ii}_${idx}`"
                :class="{ 'th-highlight': isHeaderSelected(ii, idx) }"
                style="text-transform: none; font-weight: normal;"
              >{{ res.entity.name }}
              </th>
            </template>
          </tr>
          <tr v-if="formParam.grand_total">
            <th style="vertical-align: middle;" />
            <th
              v-for="(h, hx) in formParam.column_heads"
              :key="`gt_col_${hx}`"
            >
              <div
                v-if="hx === 0"
                style="text-align: center;"
              >
                ВСЕГО
              </div>
              <div
                v-else
                style="text-align: center;"
              >
                {{ getGtValue(hx) }}
              </div>
            </th>
            <th
              v-for="(ve, i) in valueEntities"
              :key="`gt_ve_${i}`"
            >
              <div
                v-if="getTotalRecord(emptyRow, emptyRow).status === 'loading'"
                style="text-align: center"
              >
                <small class="text-secondary" />
              </div>
              <div
                v-else
                style="text-align: right;"
              >
                {{ formatNumber(totalCellValue(emptyRow, emptyRow, ve)) }}
              </div>
            </th>
            <template v-for="(col, ii) in getColumns()">
              <th
                v-for="(ve, i) in valueEntities"
                :key="`gt_${ii}_${i}`"
                :class="{ 'th-highlight': isHeaderSelected(ii, i) }"
              >
                <div
                  v-if="getTotalRecord(emptyRow, col).status === 'loading'"
                  style="text-align: center;"
                >
                  <small class="text-secondary" />
                </div>
                <div
                  v-else
                  style="text-align: right;"
                >
                  {{ formatNumber(totalCellValue(emptyRow, col, ve)) }}
                </div>
              </th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, rx) in getRows()"
            :key="`row_${rx}`"
            :data-rx="rx"
            :class="{ 'row-selected': selectedCell && selectedCell.rx === rx, 'row-dirty': row.mode === 'record' && isRowDirty(row) }"
            :title="row.mode === 'record' && isRowDirty(row) ? 'Строка не сохранена' : null"
            :style="{ 'z-index': (selectedCell && selectedCell.rx === rx) ? 99 : row.zIndex, position: 'relative' }"
          >
            <td @click="onClickRowExpand(row, 'row')">
              <div
                v-if="row.level < maxRowLevel() || row.isFolder"
                :class="`ml-${row.level_with_folders - 1}`"
              >
                <feather-icon
                  :icon="row.isExpanded ? 'MinusSquareIcon' : 'PlusSquareIcon'"
                  class="cursor-pointer"
                />
              </div>
              <div
                v-else-if="row.mode === 'record' || row.mode === 'tree_record'"
                style="text-align: center;"
              >
                <small
                  class="text-muted"
                  :title="row.record.id.value ? `id ${row.record.id.value}` : 'Новая строка'"
                  :data-id="row.record.id.value || ''"
                >{{ rowOrdinal(row) }}</small>
              </div>
            <!--{{ row.status }}-->
            </td>
            <td
              v-for="(row_cell, rcx) in row.cells"
              :key="`row_cell_${rcx}`"
              :ref="`td_${rx}_${rcx}_-1`"
              :class="getClassCellSelected(rx, rcx, row, row_cell)"
              :style="row.record && row.record.status === 'locked' ? { opacity: '0.7', 'pointer-events': 'none' } : {}"
              @click="onCellClick(rx, rcx, -1, null, row, row_cell, null, false)"
            >
              <!--<div :class="!row_cell.cfgColumn.indent ? `ml-${row.level_with_folders - 1}` : ''">{{ row_cell.title }}</div>-->
              <div :style="indentStyle(row, row_cell)">
                <div
                  v-if="(row.mode === 'record' || row.mode === 'tree_record') && row_cell && row_cell.column.cfgColumn.editable === true && canEditRow(row)"
                  class="editor"
                >
                  <!-- bit/checkbox: render as a real checkbox always (not only when active),
                       so the cell shows a tick/empty box instead of raw true/false text;
                       numeric fields with rendertype=checkbox behave the same (click = 1/NULL) -->
                  <cbs-cell-editable
                    v-if="row_cell.column.qryColumn.datatype === 'bit' || row_cell.column.qryColumn.rendertype === 'checkbox'"
                    :ref="`cell_${rx}_${rcx}_${-1}`"
                    :record="row.record"
                    :key-value="row_cell.column.qryColumn.entitysid"
                    :value-type="row_cell.column.qryColumn.datatype"
                    :render-type="row_cell.column.qryColumn.rendertype || ''"
                    :access="row.record && row.record.status === 'locked' ? 'ro' : 'rw'"
                    :align="row_cell.column.cfgColumn.align || null"
                    @update="(value) => onUpdateRowRecord(row, row_cell.column.qryColumn.entitysid, value)"
                  />
                  <template v-else-if="isActiveCell(rx, rcx, -1)">
                    <cbs-reference-picker3
                      v-if="row_cell.column.qryColumn.datatype === 'ref'"
                      :ref="`cell_${rx}_${rcx}_${-1}p`"
                      :record="row.record"
                      :field="{ entityid: row_cell.column.qryColumn.entityid, entitysid: row_cell.column.qryColumn.entitysid }"
                      :prop-ref="getRef(row_cell.column.qryColumn.entityid, row)"
                      :align="row_cell.column.cfgColumn.align || null"
                      key-mode="sid"
                      @updateCell="(value) => onUpdateRowRecord(row, row_cell.column.qryColumn.entitysid, value.value, value.title)"
                    />
                    <cbs-cell-editable
                      v-else
                      :ref="`cell_${rx}_${rcx}_${-1}`"
                      :record="row.record"
                      :key-value="row_cell.column.qryColumn.entitysid"
                      :value-type="row_cell.column.qryColumn.datatype"
                      :access="row.record && row.record.status === 'locked' ? 'ro' : 'rw'"
                      :align="row_cell.column.cfgColumn.align || null"
                      @update="(value) => onUpdateRowRecord(row, row_cell.column.qryColumn.entitysid, value)"
                    />
                  </template>
                  <template v-else>
                    <div :style="getRowRecordCellStyle(row_cell)">
                      {{ displayTruncated(formatRowCell(row, row_cell), isExpandedCell(rx, rcx, -1), 70) }}
                    </div>
                  </template>
                </div>
                <div v-else>
                  <div :style="getRowRecordCellStyle(row_cell)">
                    {{ displayTruncated(formatRowCell(row, row_cell), isExpandedCell(rx, rcx, -1), 70) }}
                  </div>
                </div>
              </div>
            </td>
            <template v-if="formParam.grand_total">
              <td
                v-for="(ve, i) in valueEntities"
                :key="`gt_ve_${i}`"
              >
                <div
                  v-if="getTotalRecord(row, emptyRow).status === 'loading'"
                  style="text-align: center"
                >
                  <small class="text-secondary" />
                </div>
                <div
                  v-else
                  style="text-align: right;"
                >
                  {{ formatNumber(totalCellValue(row, emptyRow, ve)) }}
                </div>
              </td>
            </template>
            <template v-for="(col, ii) in getColumns()">
              <td
                v-for="(ve, i) in valueEntities"
                :key="`col_${ii}_${i}`"
                :ref="`td_${rx}_${ii}_${i}`"
                class="value-cell"
                :class="{ 'cell-selected': selectedCell && selectedCell.rx === rx && selectedCell.ii === ii && selectedCell.i === i }"
                :data-key="`${rx}|${ii}|${i}`"
                :style="valueCellStyle(row, col, ve)"
                style="text-align: right;"
                @click="onCellClick(rx, ii, i, ve, row, col, null, false)"
              >
                <div v-if="isEditable(row, col)">
                  <div
                    v-if="getCellRecord(row, col, 'val').status === 'loading'"
                    style="text-align: center"
                  >
                    <small class="text-secondary">...</small>
                  </div>
                  <div
                    v-else-if="!!ve.editable"
                    class="editor"
                  >
                    <cbs-reference-picker3
                      v-if="ve.entity.datatype.sid === 'ref'"
                      :ref="`cell_${rx}_${ii}_${i}p`"
                      :record="getCellRecord(row, col, 'cell')"
                      :field="{ entityid: ve.entity.id, entitysid: ve.entity.sid }"
                      :prop-ref="getRef(ve.entity.id)"
                      key-mode="sid"
                      @updateCell="(value) => onUpdateRecord(row, col, ve, value.value, value.title)"
                    />
                    <cbs-cell-editable
                      v-else
                      :ref="`cell_${rx}_${ii}_${i}`"
                      :record="getCellRecord(row, col, 'cell')"
                      :key-value="ve.entity.sid"
                      :value-type="ve.entity.datatype.sid"
                      :render-type="cellRenderType(row, ve)"
                      :access="cellAccess(getCellRecord(row, col, 'access'), ve)"
                      align="right"
                      @update="(value) => onUpdateRecord(row, col, ve, value)"
                    />
                  </div>
                  <div v-else>
                    {{ formatNumber(getCellRecord(row, col, 'format')[ve.entity.sid].value) }}
                  </div>
                </div>
                <div v-if="formParam.total_cell && cellType(row, col) === 'total'">
                  <div
                    style="text-align: right;"
                    :style="{ color: getTotalRecord(row, col).status === 'loading' ? '#aaa' : 'inherit' }"
                    @click="debugFilter(row, col)"
                  >
                    {{ formatNumber(totalCellValue(row, col, ve)) }}
                  </div>
                </div>
              </td>
            </template>
          </tr>

          <!--  new parent row -->
          <tr
            v-if="isNewParentRowVisible()"
            class="row-new"
            style="z-index: 0; position: relative; cursor: pointer"
            @click="newParentLine"
          >
            <td>
              <div class="plus-button">
                <feather-icon
                  icon="PlusIcon"
                  size="14"
                  class="cursor-pointer"
                />
              </div>
            </td>
            <td colspan="100">
              Новая запись
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <cbs-debug
      v-if="useCubus.isRoot() || useCubus.isTester()"
      :context="this"
    />
  </div>
</template>

<script>
import useCubus from '@/cubus/services/useCubus'
import CbsDebug from '@/cubus/components/debug/CbsDebug.vue'
import CbsRecordForm from '@/cubus/components/record/CbsRecordForm.vue'
import CbsCellEditable from '@/cubus/components/cbs-form/generic-forms/cell-editable/CbsCellEditable.vue'
import Q, { toWhereString } from '@/cubus/services/Q'
import CbsReferencePicker3 from '@/cubus/components/reference/CbsReferencePicker3.vue'
import Swal from 'sweetalert2'
import { BButton, BBadge } from 'bootstrap-vue'

export default {
  name: 'CbsPivotInput',
  components: {
    BButton,
    BBadge,
    CbsReferencePicker3,
    CbsCellEditable,
    CbsRecordForm,
    CbsDebug,
  },
  props: {
    objectFull: {
      type: Object,
      default: null,
    },
  },
  setup() {
    return {
      useCubus,
    }
  },
  data() {
    return {
      // CUB-1288: поля записи выбранного параметра (сценария) для шапки формы
      paramInfo: [],
      paramStale: null,
      paramCalcError: '',
      formParam: null,
      qryRow: null,
      qryColumn: null,
      titleKey: null,
      rows: [],
      columns: [],
      values: [],
      record: null,
      srcRecord: null,
      queries: [],
      renderSourceMaps: {},
      valueRecords: [],
      valueObj: null,
      delSafeObjects: {},
      srcAccess: {},
      srcNames: {},
      isFormParamReady: false,
      totalRecords: [],
      totalObj: null,
      emptyRow: { level: 0, record: {} },
      valueEntities: [],
      selectedCell: null,
      tableStatus: 'empty',
      loading: 0,
      plusButtonPosition: null,
      deleteButtonPosition: null,
      showDeleteButton: false,
      objects: {},
      qryRefs: {},
      scalars: {},
      entities: {},
      grandTotalRecord: {},
      _qryCache: new Map(),
      visibleCells: new Set(),
      cellObserver: null,
      _vfScheduled: false,
      _rafId: null,
      copyButtonPosition: null,
      showCopyButton: false,
      multi: null,
      expandedCells: new Set(),
      _dirtyRows: null,
      dirtyTick: 0,
      savingDirty: false,
      _lastSelectedRx: null,
      // _initOnce: false,
      totalEntities: [],
      copiedCellValue: null,
      // одношаговая отмена: последняя правка ячейки (для Ctrl+Z)
      lastEdit: null,
      refs: {},
      refRowDep: {},
      rowRefs: {},
      // Map<objectid, Set<entitysid>> — entity sids that have a notnull-style constraint, used for required-empty highlight
      constraintRequiredSids: {},
    }
  },
  computed: {
    unmetRequired() {
      if (!this.objectFull?.attributes) return []
      const rec = this.record?.record?.record || {}
      return this.objectFull.attributes
        .filter(a => a.isrequired)
        .filter(a => !(rec[a.entitysid] && rec[a.entitysid].value != null))
        .map(a => a.entitysid)
    },
    // Плоские списки видимых строк/колонок. Раньше getRows()/getColumns() были
    // методами и пересобирали всё дерево на КАЖДЫЙ вызов — а в шаблоне они зовутся
    // десятки/сотни раз за рендер (вложенные v-for), что давало фриз при
    // разворачивании папок и росло квадратично на больших формах. Теперь это
    // computed: Vue кэширует результат и пересчитывает только при реальном
    // изменении rows/columns/isExpanded/subs. Результат используется только на
    // чтение (индексация, indexOf, forEach), поэтому кэш безопасен.
    flatRows() {
      return this.getRowsRecursive(this.rows, [])
    },
    flatColumns() {
      return this.getRowsRecursive(this.columns, [])
    },
  },
  watch: {
    record: {
      handler(newVal, oldVal) {
        if (this.isRecordValid()) {
          this.reinitRecord()
        }
        this.loadParamInfo()
      },
      deep: true,
    },
    objectFull: {
      immediate: true,
      handler(v) {
        if (v) this.init().catch(console.error)
      },
    },
  },
  beforeCreate() {
    if (!(this?._dirtyRows instanceof Set)) this._dirtyRows = new Set()
  },
  created() {
    if (typeof window !== 'undefined') {
      window.addEventListener('mousedown', this._onGlobalMouseDown, true)
      window.addEventListener('beforeunload', this.onBeforeUnload)
    }
  },
  mounted() {
    document.addEventListener('keydown', this.onKeyDown)
    document.addEventListener('paste', this.onPaste)

    this._rafReposition = () => {
      cancelAnimationFrame(this._rafId)
      this._rafId = requestAnimationFrame(this.repositionFloatingButtons)
    }
    const scroller = this.$el?.querySelector?.('.pivot-wrapper')
    if (scroller) scroller.addEventListener('scroll', this._rafReposition, { passive: true })
    window.addEventListener('resize', this._rafReposition)
    // Listen to scroll events on all parent scrollable containers
    document.addEventListener('scroll', this._rafReposition, { passive: true, capture: true })
  },
  updated() {
    this.$nextTick(() => this.observeAllValueCells())
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('mousedown', this._onGlobalMouseDown, true)
      window.removeEventListener('beforeunload', this.onBeforeUnload)
    }
    document.removeEventListener('keydown', this.onKeyDown)
    document.removeEventListener('paste', this.onPaste)

    const scroller = this.$el?.querySelector?.('.pivot-wrapper')
    if (scroller && this._rafReposition) scroller.removeEventListener('scroll', this._rafReposition)
    if (this._rafReposition) {
      window.removeEventListener('resize', this._rafReposition)
      document.removeEventListener('scroll', this._rafReposition, { capture: true })
    }

    this.cellObserver?.disconnect?.()
    if (this.positionUpdateInterval) clearInterval(this.positionUpdateInterval)
    cancelAnimationFrame(this._rafId)
  },
  methods: {
    /**
     * CUB-1288: шапка формы — поля записи выбранного параметра. Настройка формы `form_param.param_info`:
     * { "<sid параметра>": { "object": "<sid объекта записи, по умолчанию = sid параметра>",
     *   "fields": ["<sid поля>", …], "built": "<поле «собран»>", "changed": "<поле «данные изменены»>",
     *   "error": "<поле текста ошибки расчёта>", "labels": { "<sid поля>": "<подпись в шапке>" } } }.
     * «После сборки менялись данные» — changed позже built (или built пусто при changed). Нет настройки — шапка прежняя.
     */
    async loadParamInfo() {
      const cfgAll = this.formParam && this.formParam.param_info
      const rec = this.record && this.record.record && this.record.record.record
      if (!cfgAll || !rec) return
      const raw = v => {
        if (!v || typeof v !== 'object') return v
        return v.value !== undefined ? v.value : v.id
      }
      const ruDate = (v, withTime) => {
        const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/)
        if (!m) return String(v)
        return `${m[3]}.${m[2]}.${m[1]}${withTime && m[4] ? ` ${m[4]}:${m[5]}` : ''}`
      }
      const shown = v => {
        if (v === null || v === undefined) return ''
        if (typeof v === 'object') return v.title || v.name || v.value || ''
        return v
      }
      const key = JSON.stringify(Object.keys(cfgAll).map(k => [k, raw(rec[k])]))
      if (key === this._paramInfoKey) return
      this._paramInfoKey = key
      const info = []; let stale = null; let calcError = ''
      // eslint-disable-next-line no-restricted-syntax
      for (const [psid, cfg] of Object.entries(cfgAll)) {
        const val = raw(rec[psid])
        if (val === null || val === undefined || val === '') continue
        const fields = (cfg && cfg.fields) || []
        try {
          // eslint-disable-next-line no-await-in-loop
          const res = await useCubus.loadQuery(this, {
            keymode: 'sid', objectsid: (cfg && cfg.object) || psid, limit: 1, offset: 0, filter: Q.and([Q.equals('id', val)]),
          })
          const qry = (res && res.query) || res
          const row = qry && qry.recordset && qry.recordset.records && qry.recordset.records[0]
          if (!row) continue
          const colOf = sid => (qry.columns || []).find(cl => cl.key === sid || cl.entitysid === sid) || {}
          // подпись поля — из param_info.labels (например, «Собран»), иначе название атрибута
          const labels = (cfg && cfg.labels) || {}
          fields.forEach(f => {
            const col = colOf(f)
            let v = shown(row[f])
            // дата по-русски: «30.09.2026 12:39» (дата-время без секунд), «30.09.2026» (дата)
            if (v !== '' && (col.datatype === 'datetime' || col.datatype === 'date')) v = ruDate(v, col.datatype === 'datetime')
            if (v !== '') info.push({ title: labels[f] || col.label || f, value: v })
          })
          if (cfg && cfg.changed) {
            const built = raw(row[cfg.built]); const changed = raw(row[cfg.changed])
            if (changed) stale = !built || String(changed) > String(built)
            else if (built) stale = false
          }
          if (cfg && cfg.error) calcError = shown(row[cfg.error]) || ''
        } catch (e) {
          console.warn('CUB-1288: контекст параметра не загружен', psid, e)
        }
      }
      if (key !== this._paramInfoKey) return
      this.paramInfo = info; this.paramStale = stale; this.paramCalcError = calcError
    },
    async init() {
      // if (this._initOnce) return
      // this._initOnce = true

      await this.initRecord()
      await this.initFormParam()
      this.loadParamInfo() // CUB-1288: параметр мог прийти из настроек формы (param_record)
      await this.initObjects()
      await this.initRenderSources()
      // Constraints define which cells are visually required (red highlight)
      this.loadConstraintsForRows()

      this.isFormParamReady = true

      if (this.isRecordValid()) {
        await this.reinitRecord()
      }
    },
    async reinitRecord() {
      if (!this.isFormParamReady) return

      this.tableStatus = 'init'

      this.valueRecords = []
      this.rows = []
      this.columns = []
      this.queries = []

      if (this.visibleCells && typeof this.visibleCells.clear === 'function') {
        this.visibleCells.clear()
      }

      this._dirtyRows = (this._dirtyRows instanceof Set) ? this._dirtyRows : new Set()

      if (this.isRecordValid()) {
        await this.initRowQueries()
        await this.initColumnQueries()
        await this.initRows()
        await this.initColumns()
        this.stripDaterangeParamFromRows()
      }

      this.tableStatus = 'ready'
    },
    // REQ-004 p.4 / BUG-017: the header date param shares its entity with the journal date,
    // so row records (header copies) carry the picked date under the same sid. A row spans
    // ALL days — only the column pins the day. Left in place, the row date mismatches the
    // column day in checkRecord for every other day, re-creating cell placeholders (and
    // queries) on every render until the page freezes; it also poisons row filters with
    // an equal-condition on the picked date.
    stripDaterangeParamFromRows() {
      const cfg = this.formParam.columns && this.formParam.columns[0]
      if (!cfg || cfg.column_mode !== 'daterange') return
      const dateEnt = this.entities[Number(cfg.column_entityid)]
      const sid = dateEnt && dateEnt.entity ? dateEnt.entity.sid : null
      if (!sid) return
      const strip = rows => rows.forEach(r => {
        if (r.record && Object.prototype.hasOwnProperty.call(r.record, sid)) this.$delete(r.record, sid)
        if (r.subs && r.subs.length) strip(r.subs)
      })
      strip(this.rows)
    },
    async initFormParam() {
      const prm = this.objectFull.params.params.find(p => p.paramname === 'form_param')
      if (prm) {
        this.formParam = prm.jsonstring
        await this.initEntities()
        if (this.formParam.param_record) {
          Object.keys(this.formParam.param_record).forEach(key => {
            this.$set(this.record.record.record, key, this.formParam.param_record[key])
          })
        }
        if (this.formParam.value_cell) {
          const vc = this.formParam.value_cell
          if (vc.objectid) {
            await this.initValueObj()
          }
          if (vc.results) this.initValueEntities()
        }
        if (this.formParam.total_cell) {
          const tc = this.formParam.total_cell
          if (tc.objectid) {
            await this.initTotalObj()
          }
          if (tc.results) this.initTotalEntities()
        }
        await this.initDelSafeObjects()
      } else {
        console.warn('No formParam found in objectFull params.')
      }
    },
    // BUG-019: data objects of the matrix that physically carry a `del` column — only
    // for them a del=false condition may be added to queries (a view cannot resolve the
    // field). Without the condition, users whose credential shows marked records
    // (admins) get deleted records rendered in cells and counted in sums.
    async initDelSafeObjects() {
      const ids = new Set()
      const rowCfgs = this.formParam.rows || []
      rowCfgs.forEach(r => { if (r.objectid) ids.add(Number(r.objectid)) })
      const colCfgs = this.formParam.columns || []
      colCfgs.forEach(c => { if (c.objectid) ids.add(Number(c.objectid)) })
      if (this.formParam.value_cell && this.formParam.value_cell.objectid) ids.add(Number(this.formParam.value_cell.objectid))
      if (this.formParam.total_cell && this.formParam.total_cell.objectid) ids.add(Number(this.formParam.total_cell.objectid))
      const physical = ['table', 'cube', 'catalog']
      await Promise.all([...ids].map(async id => {
        let obj = null
        if (this.valueObj && Number(this.formParam.value_cell?.objectid) === id) obj = this.valueObj
        else if (this.totalObj && Number(this.formParam.total_cell?.objectid) === id) obj = this.totalObj
        else obj = await useCubus.loadObject(this, { objectid: id })
        if (obj && obj.object && physical.includes(obj.object.meta)) this.$set(this.delSafeObjects, id, true)
        // BUG-781: права объекта-источника — пишет форма в него, и бэк спрашивает право у него.
        // Не загрузился (нет select) — прав нет.
        this.$set(this.srcAccess, id, (obj && obj.access) || {})
        if (obj && obj.object && obj.object.sid) this.$set(this.srcNames, obj.object.sid, obj.object.name || obj.object.sid)
      }))
    },
    withDelFilter(objectid, filter) {
      if (!this.delSafeObjects[Number(objectid)]) return filter
      const cond = Q.equals('del', false)
      if (!filter || !filter.node) return Q.and([cond])
      return {
        isactive: true,
        node: {
          isactive: true, oper: { sid: 'and' }, nodes: [filter.node], conds: [cond],
        },
      }
    },

    repositionFloatingButtons() {
      this.calculatePlusButtonPosition()
      this.calculateDeleteButtonPosition()
      this.calculateCopyButtonPosition()
    },

    collectEntityIds(rows, entityIdsSet) {
      for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i]
        if (row.results) {
          for (let j = 0; j < row.results.length; j += 1) {
            const result = row.results[j]
            if (result.source_entityid) {
              entityIdsSet.add(result.source_entityid)
            }
            if (result.destination_entityid) {
              entityIdsSet.add(result.destination_entityid)
            }
          }
        }
        // daterange matrix (REQ-004 p.4): the column source has no object, only entity ids
        if (row.column_entityid) {
          entityIdsSet.add(Number(row.column_entityid))
        }
        if (row.param_entityid) {
          entityIdsSet.add(Number(row.param_entityid))
        }
        if (row.columns) {
          for (let j = 0; j < row.columns.length; j += 1) {
            const col = row.columns[j]
            if (col.entityid) {
              entityIdsSet.add(col.entityid)
            }
            if (col.destination_entityid) {
              entityIdsSet.add(col.destination_entityid)
            }
          }
        }
      }
    },

    async initEntities() {
      const entityIdsToLoad = new Set()

      if (this.formParam.rows) {
        this.collectEntityIds(this.formParam.rows, entityIdsToLoad)
      }
      if (this.formParam.columns) {
        this.collectEntityIds(this.formParam.columns, entityIdsToLoad)
      }
      if (this.formParam.value_cell && this.formParam.value_cell.results) {
        this.formParam.value_cell.results.forEach(attr => {
          if (attr.entityid) {
            entityIdsToLoad.add(attr.entityid)
          }
          // per-row render source (REQ-004 p.2): habit-type entities must be resolvable
          if (attr.render_source) {
            if (attr.render_source.key_entityid) entityIdsToLoad.add(Number(attr.render_source.key_entityid))
            if (attr.render_source.render_entityid) entityIdsToLoad.add(Number(attr.render_source.render_entityid))
          }
        })
      }
      if (this.formParam.total_cell && this.formParam.total_cell.results) {
        this.formParam.total_cell.results.forEach(attr => {
          if (attr.entityid) {
            entityIdsToLoad.add(attr.entityid)
          }
        })
      }

      const ids = Array.from(entityIdsToLoad)
      await this.loadEntities(ids)
    },
    cellKey(rx, ii, i) {
      return `${rx}|${ii}|${i}`
    },
    isExpandedCell(rx, ii, i) {
      return this.expandedCells.has(this.cellKey(rx, ii, i))
    },
    displayTruncated(text, expanded, limit = 50) {
      if (text == null) return ''
      const s = String(text)
      if (expanded || s.length <= limit) return s
      return `${s.slice(0, limit)}…`
    },
    async loadEntity(entityid) {
      const res = await useCubus.loadEntity(this, { entityid })
      this.$set(this.entities, entityid, res)
    },
    stableKey(params) {
      const {
        objectid, filter, groupby, orderby, paramrecord,
      } = params
      const where = filter ? toWhereString(filter) : ''
      return [
        objectid,
        where,
        JSON.stringify(groupby || null),
        JSON.stringify(orderby || null),
        JSON.stringify(paramrecord?.record || null),
      ].join('|')
    },
    isActiveCell(rx, ii, i) {
      const s = this.selectedCell
      return !!(s && s.rx === rx && s.ii === ii && s.i === i)
    },
    async loadQueryCached(params) {
      const k = this.stableKey(params)
      const cached = this._qryCache.get(k)
      if (cached) return cached
      const p = useCubus.loadQuery(this, params).then(res => {
        this._qryCache.set(k, res)
        return res
      })
      this._qryCache.set(k, p)
      return p
    },
    scheduleVisibleFetch() {
      if (this._vfScheduled) return
      this._vfScheduled = true
      const ric = window.requestIdleCallback || (cb => setTimeout(cb, 0))
      ric(() => {
        this._vfScheduled = false
        this.fetchVisibleCells()
      })
    },
    async fetchVisibleCells() {
      // eslint-disable-next-line no-restricted-syntax
      for (const key of this.visibleCells) {
        const [rx, ii, i] = key.split('|').map(Number)
        const row = this.getRows()[rx]
        const col = this.getColumns()[ii]
        this.getCellRecord(row, col)
      }
    },
    async loadEntities(entityIds) {
      const list = await useCubus.loadEntities(this, { entityids: entityIds })
      // console.log('loadEntities', list.entities)
      for (let i = 0; i < list.entities.length; i += 1) {
        const ent = list.entities[i]
        if (!Object.hasOwn(this.entities, ent.id)) {
          this.$set(this.entities, ent.id, { entity: ent })
        }
      }
    },

    mapResultsToRecord(rowHere, rowColCfg, rec) {
      // const pairs = this.normalizeResultPairs(rowColCfg)
      let pairs = []
      if (rowColCfg.result) pairs = [rowColCfg.result]
      if (rowColCfg.results) pairs = rowColCfg.results
      // const pairs = rowColCfg.results ? rowColCfg.results : (rowColCfg.result ? [rowColCfg.result] : [])
      // eslint-disable-next-line no-restricted-syntax
      for (const pair of pairs) {
        const destEnt = this.entities[pair.destination_entityid]
        const srcEnt = this.entities[pair.source_entityid]
        const srcCell = rowHere[srcEnt.entity.sid]
        if (srcCell) {
          let { title } = srcCell
          if (srcEnt.entity.sid === 'id') {
            const srcNameCell = rowHere.name
            if (srcNameCell) {
              title = srcNameCell.value
            }
          }
          this.$set(rec, destEnt.entity.sid, {
            value: srcCell.value,
            title,
            mode: rowColCfg.mode || 'list',
          })
        } else {
          this.$set(rec, destEnt.entity.sid, {
            value: null,
            title: null,
            mode: rowColCfg.mode || 'list',
          })
        }
      }
      if (rowColCfg.mode === 'record' || rowColCfg.mode === 'tree_record') {
        this.$set(rec, 'id', rowHere.id)
      }
      return rec
    },

    async initRecord() {
      this.record = await useCubus.loadNewRecord(this, {
        objectid: this.objectFull.object.id,
        keymode: 'sid',
      })
      this.srcRecord = JSON.parse(JSON.stringify(this.record))
    },
    isRecordValid() {
      if (!this.record) return false
      if (this.objectFull.attributes.some(a => a.isrequired && !this.record.record.record[a.entitysid].value)) return false
      return true
    },
    updateShowPlusButton(row) {
      // this.showPlusButton = !(row.level < this.maxRowLevel() || row.isFolder)
      const parent = this.getRowParent(row)
      let show = !!row.addrecord || (parent && !!parent.addrecord)
      // Show plus on group rows if the sub-level allows adding
      if (!show && row.level < this.maxRowLevel() && row.cfg) {
        const subCfg = this.getCfgForSubrows('row', row)
        if (subCfg) {
          // Sub-level is another group → allow adding sub-groups
          if (subCfg.mode !== 'record' && subCfg.mode !== 'tree_record') {
            show = true
          }
          // Sub-level is record with addrecord → allow adding records inside this group
          if ((subCfg.mode === 'record' || subCfg.mode === 'tree_record') && subCfg.addrecord) {
            show = true
          }
        }
      }
      if (show) {
        const subCfg = (row.mode === 'record' || row.mode === 'tree_record') ? null : this.getCfgForSubrows('row', row)
        show = this.canDo('insert', subCfg?.objectid ?? row.cfg?.objectid)
      }
      this.showPlusButton = show
    },
    updateShowDeleteButton(row) {
      this.showDeleteButton = row.mode === 'record' && row.status !== 'loading' && this.canDo('delete', row.cfg?.objectid)
    },
    isGranted(access, perm) {
      return !!access && ['grant', 'withgrant'].includes(access[perm])
    },
    // BUG-781: действие в форме разрешено, если форма открыта на правку (update формы, как
    // прежде) И объект-источник, куда оно пишет, даёт это право (update / insert / delete).
    // Раньше смотрели только на форму: наблюдатель видел редакторы и корзину, а сохранение
    // отбивалось отказом бэка.
    canDo(perm, objectid) {
      if (!this.isGranted(this.objectFull?.access, 'update')) return false
      if (objectid == null) return true
      const src = this.srcAccess[Number(objectid)]
      return src === undefined ? true : this.isGranted(src, perm)
    },
    // BUG-784: в «#» — порядковый номер строки в своей группе, а не внутренний id (он в подсказке).
    rowOrdinal(row) {
      if (!row?.record?.id?.value) return ''
      const siblings = (row.parentRow ? row.parentRow.subs : this.rows) || []
      const saved = siblings.filter(r => (r.mode === 'record' || r.mode === 'tree_record') && r.record?.id?.value)
      const n = saved.indexOf(row)
      return n >= 0 ? n + 1 : ''
    },
    isNewRowRecord(row) {
      const id = row?.record?.id
      return !id || id.value == null || id.value === 0
    },
    canEditRow(row) {
      return this.canDo(this.isNewRowRecord(row) ? 'insert' : 'update', row?.cfg?.objectid)
    },
    isHeaderSelected(ii, i) {
      if (!this.selectedCell) return false
      // const { ii } = this.selectedCell
      if (i >= 0) {
        return this.selectedCell.ii === ii && this.selectedCell.i === i
      }
      return this.selectedCell.ii === ii && this.selectedCell.i === -1
    },
    onCellClick(rx, ii, i, ve, row, col, seed, startEdit = false) {
      if (this._lastSelectedRx != null && this._lastSelectedRx !== rx) {
        this.commitRowIfNeeded(this._lastSelectedRx)
      }
      this._lastSelectedRx = rx
      this.selectedCell = { rx, ii, i }

      this.expandedCells.clear()
      this.expandedCells.add(this.cellKey(rx, ii, i))

      this.$nextTick(() => {
        this.updateShowPlusButton(this.getRows()[rx])
        this.updateShowDeleteButton(this.getRows()[rx])
        this.updateShowCopyButton(this.getRows()[rx])
        this.calculateCopyButtonPosition()
        this.calculatePlusButtonPosition()
        this.calculateDeleteButtonPosition()
      })

      if (ve && !ve.editable) return
      if (!this.isEditable(row, col, i, ii)) return

      const refName = `cell_${rx}_${ii}_${i}`
      const ref = this.$refs[refName] || this.$refs[`${refName}p`]
      if (!ref) return
      const comp = Array.isArray(ref) ? ref[0] : ref
      if (startEdit && comp?.editOn) {
        comp.editOn(false)
        if (seed != null) {
          this.$nextTick(() => {
            const el = comp.$el.querySelector('[contenteditable="true"]')
            if (!el) return
            el.textContent = seed
            const sel = window.getSelection()
            const range = document.createRange()
            range.selectNodeContents(el)
            range.collapse(false)
            sel.removeAllRanges()
            sel.addRange(range)
            el.dispatchEvent(new InputEvent('input', { bubbles: true }))
            el.focus()
          })
        }
      }
    },
    async loadQuery(vm, params) {
      this.loading += 1
      const p = params && params.objectid
        ? { ...params, filter: this.withDelFilter(params.objectid, params.filter) }
        : params
      const result = await useCubus.loadQuery(vm, p)
      this.loading -= 1
      return result
    },
    observeAllValueCells() {
      if (!this.cellObserver) return
      const wrapper = this.$el?.querySelector('.pivot-wrapper')
      if (!wrapper) return
      const nodes = wrapper.querySelectorAll('td.value-cell')
      nodes.forEach(n => { this.cellObserver.observe(n) })
    },
    async initRowQueries() {
      if (this.formParam && this.formParam.rows && this.formParam.rows.length > 0) {
        const cfg = this.formParam.rows[0]

        let combinedFilter = Q.and([])
        if (cfg.filter) {
          if (cfg.mode && (cfg.mode === 'tree' || cfg.mode === 'tree_record')) {
            // combinedFilter = Q.and([...cfg.filter.node.conds, Q.isNull('parent')])
            combinedFilter = {
              node: {
                isactive: true,
                oper: { sid: 'and' },
                nodes: [
                  {
                    isactive: true, oper: { sid: 'and' }, nodes: [], conds: [Q.isNull('parent')],
                  },
                  cfg.filter.node,
                ],
              },
            }
          } else {
            // combinedFilter = Q.and([...cfg.filter.node.conds])
            combinedFilter = {
              node: {
                isactive: true,
                oper: { sid: 'and' },
                nodes: [cfg.filter.node],
              },
            }
          }
          // combinedFilter = Q.and([...cfg.filter.node.conds, Q.isNull('parent')])
          // console.log('combinedFilter', combinedFilter)
        }

        const qry = await this.loadQuery(this, {
          keymode: 'sid',
          objectid: Number(cfg.objectid),
          filter: combinedFilter,
          groupby: cfg.groupby || null,
          orderby: cfg.orderby || null,
          limit: 1000,
          paramRecord: {
            keymode: 'sid',
            record: this.record.record.record,
          },
        })
        this.queries.push({ label: 'row_1', query: qry })
      }
    },
    async initColumnQueries() {
      if (this.formParam && this.formParam.columns) {
        const key = 'col_1'
        const cfg = this.formParam.columns[0]
        // daterange columns are generated locally from the header date — no column object to query
        if (cfg.column_mode === 'daterange') return
        const qry = await this.loadQuery(this, {
          keymode: 'sid',
          objectid: Number(cfg.objectid),
          filter: cfg.filter || null,
          groupby: cfg.groupby || null,
          orderby: cfg.orderby || null,
          paramRecord: {
            keymode: 'sid',
            record: this.record.record.record,
          },
        })
        this.queries.push({ label: key, query: qry })
      }
    },

    async initRows() {
      const rowCfg = this.formParam.rows[0]
      const qry = this.getQuery('row_1')

      // сопоставляем конфиг-колонки с колонками запроса
      const keys = rowCfg.columns.map(c => {
        const col = qry.columns.find(cl => cl.entityid === Number(c.entityid))
        return { qryColumn: col || null, cfgColumn: c }
      })

      const rowRecords = []
      const multiParams = []

      for (let i = 0; i < qry.recordset.records.length; i += 1) {
        const row = qry.recordset.records[i]
        // eslint-disable-next-line no-await-in-loop
        const resultRec = await this.getRowRecord(null, row, rowCfg)
        rowRecords.push({ row, resultRec, index: i })

        for (let j = 0; j < keys.length; j += 1) {
          const key = keys[j]
          if (!key || !key.cfgColumn) continue

          const name = `rowindex_${i}_keyindex_${j}`

          if (key.cfgColumn.type === 'scalar') {
            multiParams.push({
              name,
              method: 'scalarvalue',
              mode: 'sync',
              param: { scalarid: key.cfgColumn.scalarid || null, record: resultRec || null },
            })
          } else if (key.cfgColumn.type === 'aggr_sum') {
            // самый нижний уровень строк у формы
            const formRow = this.formParam.rows[this.formParam.rows.length - 1]

            // const flt = this.getFilterForAggrSum_v1(0, row)
            const flt = this.getFilterForAggrSum(this.formParam.rows[0], row)
            // console.log('flt', flt)

            const resultGroup = {
              isactive: true,
              groups: [{ isactive: true, oper: { sid: 'sum' }, entity: { value: key.cfgColumn.destination_entityid } }],
            }
            multiParams.push({
              name,
              method: 'query',
              mode: 'sync',
              param: {
                objectid: this.formParam.rows[this.formParam.rows.length - 1].objectid,
                // filter: formRow.filter,
                filter: this.withDelFilter(this.formParam.rows[this.formParam.rows.length - 1].objectid, flt),
                // groupby: formRow.groupby,
                groupby: resultGroup,
                orderby: formRow.orderby,
                limit: 1,
                paramrecord: { record: resultRec || null, keymode: 'sid' },
                keymode: 'sid',
              },
            })
          }
        }
      }

      const multi = await useCubus.loadMultiQuery(this, { requests: multiParams })
      const multiResults = (multi && multi.result && multi.result.results) || []

      for (let i = 0; i < rowRecords.length; i += 1) {
        const rec = rowRecords[i]
        const { row } = rec
        const cells = []

        for (let j = 0; j < keys.length; j += 1) {
          const key = keys[j]

          if (key && key.qryColumn) {
            const cell = row[key.qryColumn.entitysid]
            cells.push({
              title: (cell && (cell.title != null ? cell.title : cell.value)) || null,
              value: (cell && cell.value) != null ? cell.value : null,
              column: key,
              mode: rowCfg.mode || 'list',
            })
            continue
          }

          if (key && key.cfgColumn && key.cfgColumn.type === 'scalar') {
            const name = `rowindex_${rec.index}_keyindex_${j}`
            const idx = multiResults.findIndex(r => r.name === name)
            const sclr = idx >= 0 ? multiResults[idx].result : null
            const scell = sclr && sclr.cell
            cells.push({
              title: scell ? (scell.title != null ? scell.title : scell.value) : null,
              value: scell ? scell.value : null,
              column: key,
              mode: rowCfg.mode || 'list',
              scalarvalue: sclr || null,
            })
            continue
          }

          if (key && key.cfgColumn && key.cfgColumn.type === 'aggr_sum') {
            const name = `rowindex_${rec.index}_keyindex_${j}`
            const idx = multiResults.findIndex(r => r.name === name)
            const res = idx >= 0 ? multiResults[idx].result : null
            if (res) {
              const qRec = res.query && res.query.recordset && res.query.recordset.records
                ? res.query.recordset.records[0]
                : null
              const ent = this.entities[key.cfgColumn.destination_entityid]
              const destSid = ent && ent.entity ? ent.entity.sid : null
              const qCell = destSid && qRec ? qRec[destSid] : null
              cells.push({
                title: (qCell ? (qCell.title != null ? qCell.title : qCell.value) : null),
                value: qCell ? qCell.value : null,
                column: key,
                mode: rowCfg.mode || 'list',
                scalarvalue: res,
              })
            } else {
              cells.push({
                title: null, value: null, column: key, mode: rowCfg.mode || 'list',
              })
            }
            continue
          }

          cells.push({
            title: null,
            value: null,
            column: key,
            mode: rowCfg.mode || 'list',
          })
        }

        this.rows.push({
          level: 1,
          level_with_folders: 1,
          isExpanded: false,
          record: rec.resultRec,
          cells,
          subs: [],
          status: 'ready',
          subStatus: 'empty',
          isFolder: row && row.isfolder ? row.isfolder.value : null,
          parent: row && row.parent ? row.parent.value : null,
          id: row && row.id ? row.id.value : null,
          addrecord: rowCfg.addrecord || false,
          zIndex: 1,
          cfg: rowCfg,
          parentRow: null,
          mode: rowCfg.mode || 'list',
          lastChange: null,
          subsQuery: null,
        })
      }

      this.recalculateZIndices()
      await this.initRefs(qry)
    },

    getFilterForAggrSum_v1(rowCfgIndex, row) {
      const rowCfg = this.formParam.rows[rowCfgIndex]

      // const formRow = this.formParam.rows[1]

      // самый нижний уровень строк у формы
      const formRow = this.formParam.rows[this.formParam.rows.length - 1]

      // создаем условия вПапке для иерархических уровней
      const conditions = rowCfg.results.map(r => ({
        isactive: true,
        oper: { sid: rowCfg.mode === 'tree' ? 'inbranch' : 'equal' },
        args: [
          { type: { sid: 'entity' }, value: r.destination_entityid },
          { type: { sid: 'value' }, value: row[this.entities[r.source_entityid]?.entity.sid]?.value || null },
        ],
      }))

      const condsExlcEqual = []
      for (let k = 0; k < formRow.filter.node.conds.length; k += 1) {
        const cond = formRow.filter.node.conds[k]
        let crit = true
        for (let l = 0; l < rowCfg.results.length; l += 1) {
          const r = rowCfg.results[l]
          if (cond.oper.sid === 'equal' && cond.args[0].type.sid === 'entity' && cond.args[0].value === r.destination_entityid) {
            crit = false
            break
          }
        }
        if (crit) condsExlcEqual.push(cond)
      }
      const node = {
        isactive: true,
        oper: formRow.filter.node.oper,
        nodes: formRow.filter.node.nodes || [],
        conds: condsExlcEqual,
      }

      // собираем фильтр
      const flt = {
        isactive: true,
        node: {
          isactive: true,
          oper: { sid: 'and' },
          nodes: [
            node,
          ],
          conds: conditions,
        },
      }
      // console.log('flt', flt)
      return flt
    },

    // сборка фильтра для aggr_sum
    getFilterForAggrSum(rowCfg, row) {
      // rowCfgIndex - уровень строки, для которой собирается фильтр
      // row - запись, для которой собирается фильтр
      // rowCfg - конфигурация текущего уровня строк
      // const rowCfg = this.formParam.rows[rowCfgIndex]
      const rowCfgIndex = this.formParam.rows.indexOf(rowCfg)
      // самый нижний уровень строк у формы
      const formRow = this.formParam.rows[this.formParam.rows.length - 1]

      // создаем условия вПапке для иерархических уровней
      const conditions = rowCfg.results.map(r => ({
        isactive: true,
        oper: { sid: rowCfg.mode === 'tree' ? 'inbranch' : 'equal' },
        args: [
          { type: { sid: 'entity' }, value: r.destination_entityid },
          { type: { sid: 'value' }, value: row[this.entities[r.source_entityid]?.entity.sid]?.value || null },
        ],
      }))

      // исключаем из условий нижнего уровня условия, которые ссылаются на paramentity верхних уровней
      const lowestLevelRowConditions = JSON.parse(JSON.stringify(formRow.filter.node.conds))
      for (let i = rowCfgIndex; i < this.formParam.rows.length; i += 1) {
        const crsRowCfg = this.formParam.rows[i]
        if (crsRowCfg.results) {
          for (let j = 0; j < crsRowCfg.results.length; j += 1) {
            const crsResult = crsRowCfg.results[j]
            for (let k = 0; k < lowestLevelRowConditions.length; k += 1) {
              const crsCond = lowestLevelRowConditions[k]
              if (crsCond.oper.sid === 'equal'
                  && crsCond.args[0].type.sid === 'entity'
                  && crsCond.args[1].type.sid === 'paramentity'
                  && crsCond.args[1].value === crsResult.destination_entityid) {
                lowestLevelRowConditions.splice(k, 1)
              }
            }
          }
        }
      }

      const node = {
        isactive: true,
        oper: formRow.filter.node.oper,
        nodes: formRow.filter.node.nodes || [],
        conds: lowestLevelRowConditions,
      }

      // собираем фильтр
      const flt = {
        isactive: true,
        node: {
          isactive: true,
          oper: { sid: 'and' },
          nodes: [
            node,
          ],
          conds: conditions,
        },
      }
      // console.log('flt', flt)
      return flt
    },

    initColumns() {
      if (this.formParam.columns) {
        const cfg = this.formParam.columns[0]
        if (cfg.column_mode === 'daterange') {
          this.initDaterangeColumns(cfg)
          return
        }
        const qry = this.getQuery('col_1')
        const keys = cfg.columns.map(c => {
          const col = qry.columns.find(cl => cl.entityid === Number(c.entityid))
          // return col ? col.entitysid : null
          return { qryColumn: col || null, cfgColumn: c }
        })
        // eslint-disable-next-line no-restricted-syntax
        for (const row of qry.recordset.records) {
          const cells = []
          keys.forEach(key => {
            if (key && key.qryColumn) {
              // const cell = row[key]
              const cell = row[key.qryColumn.entitysid]
              // cells.push(cell.title ? cell.title : cell.value)
              cells.push({ title: cell.title ? cell.title : cell.value, column: key })
            } else {
              cells.push(null)
            }
          })

          const rec = JSON.parse(JSON.stringify(this.record.record.record))
          // eslint-disable-next-line no-await-in-loop
          this.mapResultsToRecord(row, cfg, rec)

          this.columns.push({
            level: 1,
            level_with_folders: 1,
            isExpanded: false,
            record: rec,
            cells,
            subs: [],
            status: 'ready',
            subStatus: 'empty',
            lastChange: null,
          })
        }
      }
    },

    // REQ-004 p.4 (daterange matrix): one column per day of the period around the header
    // date param. Column config: { column_mode: 'daterange', column_entityid, period, param_entityid }.
    // The day lands in col.record with mode 'day', so cell filters use the 'inday' condoper
    // and new cell records inherit the day as their date.
    initDaterangeColumns(cfg) {
      const dateEnt = this.entities[Number(cfg.column_entityid)]
      if (!dateEnt || !dateEnt.entity) return
      const dateSid = dateEnt.entity.sid
      const anchor = this.daterangeAnchor(cfg)
      this.daterangeDays(cfg.period || 'month', anchor).forEach(day => {
        const rec = JSON.parse(JSON.stringify(this.record.record.record))
        rec[dateSid] = { value: this.formatDayIso(day), title: this.formatDayTitle(day), mode: 'day' }
        this.columns.push({
          level: 1,
          level_with_folders: 1,
          isExpanded: false,
          record: rec,
          cells: [{ title: String(day.getDate()), column: null }],
          subs: [],
          status: 'ready',
          subStatus: 'empty',
          lastChange: null,
        })
      })
    },
    daterangeAnchor(cfg) {
      const prm = this.entities[Number(cfg.param_entityid || cfg.column_entityid)]
      const sid = prm && prm.entity ? prm.entity.sid : null
      const cell = sid ? this.record.record.record[sid] : null
      const d = cell && cell.value ? new Date(String(cell.value).slice(0, 10)) : new Date()
      return Number.isNaN(d.getTime()) ? new Date() : d
    },
    daterangeDays(period, anchor) {
      const days = []
      if (period === 'week') {
        const start = new Date(anchor)
        start.setDate(anchor.getDate() - ((anchor.getDay() + 6) % 7))
        for (let i = 0; i < 7; i += 1) {
          const d = new Date(start)
          d.setDate(start.getDate() + i)
          days.push(d)
        }
      } else {
        const lastDay = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate()
        for (let i = 1; i <= lastDay; i += 1) {
          days.push(new Date(anchor.getFullYear(), anchor.getMonth(), i))
        }
      }
      return days
    },
    formatDayIso(d) {
      const p = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    },
    formatDayTitle(d) {
      return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    // day-mode cells compare by calendar day: the column key is YYYY-MM-DD while the
    // stored record value is a full datetime
    cellValueMatches(recCell, refCell) {
      if (refCell && refCell.mode === 'day' && recCell.value !== null && refCell.value !== null) {
        return String(recCell.value).slice(0, 10) === String(refCell.value).slice(0, 10)
      }
      return recCell.value === refCell.value
    },
    // bounds the bulk row query to the visible period so a long journal is not fetched whole
    daterangeFilterNode() {
      const cfg = this.formParam.columns && this.formParam.columns[0]
      if (!cfg || cfg.column_mode !== 'daterange' || this.columns.length === 0) return null
      const dateEnt = this.entities[Number(cfg.column_entityid)]
      if (!dateEnt || !dateEnt.entity) return null
      const { sid } = dateEnt.entity
      const { value: first } = this.columns[0].record[sid]
      const { value: last } = this.columns[this.columns.length - 1].record[sid]
      return {
        isactive: true,
        oper: { sid: 'and' },
        nodes: [],
        conds: [
          {
            isactive: true,
            oper: { sid: 'notless' },
            args: [
              { type: { sid: 'entitysid' }, value: sid },
              { type: { sid: 'value' }, value: first },
            ],
          },
          {
            isactive: true,
            oper: { sid: 'notgreater' },
            args: [
              { type: { sid: 'entitysid' }, value: sid },
              { type: { sid: 'value' }, value: `${last} 23:59:59` },
            ],
          },
        ],
      }
    },

    getQuery(key) {
      return this.queries.find(q => q.label === key)?.query || null
    },
    maxRowLevel() {
      return this.formParam.rows.length
    },
    maxColumnLevel() {
      return this.formParam.columns.length
    },
    onClickRowExpand(row, mode) {
      if (!this.isRowExpandable(row, mode)) return false
      const srcRow = row

      let selectedRow = null

      if (this.selectedCell) selectedRow = this.getRows()[this.selectedCell.rx]

      srcRow.isExpanded = !srcRow.isExpanded
      /*
      if (srcRow.isExpanded && srcRow.subStatus === 'empty') {
        if (srcRow.isFolder) {
          const cfg = mode === 'row' ? this.formParam.rows[srcRow.level - 1] : this.formParam.columns[srcRow.level - 1]
          this.initSubRows(row, cfg)
        } else {
          const cfg = mode === 'row' ? this.formParam.rows[srcRow.level] : this.formParam.columns[srcRow.level]
          this.initSubRows(row, cfg)
        }
      }
      */
      if (srcRow.isExpanded) {
        const cfg = this.getCfgForSubrows(mode, srcRow)
        this.initSubRows(srcRow, cfg)
      } else if (selectedRow) {
        // check if the selected row still visible
        let stillVisible = false
        this.getRows().forEach((r, idx) => {
          if (r === selectedRow && idx === this.selectedCell.rx) {
            stillVisible = true
          }
        })
        if (!stillVisible) {
          const newRx = this.getRows().indexOf(srcRow)
          this.onCellClick(newRx, 0, -1, null, srcRow, null, null, false)
        }
      }
      this.recalculateZIndices()
      return true
    },
    getCfgForSubrows(mode, srcRow) {
      const configs = mode === 'row' ? this.formParam.rows : this.formParam.columns
      const idx = srcRow.isFolder ? (srcRow.level - 1) : (srcRow.level) // level=1 -> idx=1 (второй конфиг)
      return configs[idx] || null
    },
    isRowExpandable(row, mode) {
      let ok = false
      if (mode === 'row' && row.level < this.maxRowLevel()) ok = true
      if (mode === 'col' && row.level < this.maxColumnLevel()) ok = true
      if (row.isFolder) ok = true
      return ok
    },
    getRows() {
      // Возвращаем кэшированный computed (см. flatRows). Все вызовы getRows() —
      // на чтение, поэтому общий кэшируемый массив безопасен.
      return this.flatRows
    },
    getColumns() {
      return this.flatColumns
    },
    getRowsRecursive(rows, res) {
      rows.forEach(row => {
        res.push(row)
        if (row.isExpanded && row.subs.length > 0) {
          this.getRowsRecursive(row.subs, res)
        }
      })
      return res
    },

    async initSubRows(parentRow, cfg) {
      const p = parentRow
      // console.log(p.subStatus)
      if (p.subStatus !== 'empty') return
      p.subStatus = 'loading'
      const qry = await this.getRowQry(p, cfg)
      p.subsQuery = qry
      const rowColumns = this.getRowColumns(cfg, qry)
      // eslint-disable-next-line no-restricted-syntax
      for (const row of qry.recordset.records) {
        // eslint-disable-next-line no-await-in-loop
        const rec = this.getRowRecord(p, row, cfg)
        // eslint-disable-next-line no-await-in-loop
        const cells = await this.getRowCells(rowColumns, row, cfg, rec, parentRow)

        p.subs.push({
          level: p.isFolder ? p.level : p.level + 1,
          level_with_folders: p.level_with_folders + 1,
          isExpanded: false,
          record: rec,
          cells,
          subs: [],
          status: 'ready',
          subStatus: 'empty',
          isFolder: row.isfolder?.value,
          parent: row.parent?.value,
          subParent: p,
          id: row.id?.value,
          addrecord: cfg.addrecord || false,
          mode: cfg.mode || 'list',
          cfg,
          zIndex: 1,
          parentRow: p,
          subsQuery: null,
        })
        this.recalculateZIndices()
      }
      await this.initRefs(qry)
      p.subStatus = 'loaded'
    },
    getCombinedFilter(parentRow, cfg) {
      if (!parentRow) return cfg.filter
      let combinedFilter = Q.and([Q.equals('parent', parentRow.id)])
      if (cfg.filter) {
        combinedFilter = Q.and([...cfg.filter.node.conds, Q.equals('parent', parentRow.id)])
      }
      if (!parentRow.isFolder) {
        combinedFilter = cfg.filter
      }
      return combinedFilter
    },

    async getRowQry(parentRow, cfg) {
      return this.loadQuery(this, this.getRowQryParam(parentRow, cfg))
    },
    getRowQryParam(parentRow, cfg) {
      return {
        keymode: 'sid',
        objectid: Number(cfg.objectid),
        filter: this.getCombinedFilter(parentRow, cfg),
        groupby: cfg.groupby || null,
        orderby: cfg.orderby || null,
        limit: 1000,
        paramRecord: { keymode: 'sid', record: parentRow ? parentRow.record : this.record.record.record },
      }
    },

    getRowColumns(cfg, qry) {
      return cfg.columns.map(c => {
        const col = qry.columns.find(cl => cl.entityid === Number(c.entityid))
        return { qryColumn: col || null, cfgColumn: c }
      })
    },
    async getRowCells(rowColumns, record, rowCfg, paramRecord, parentRow) {
      const cells = []
      const multiParams = []
      // console.log('record', record)
      // console.log('rowCfg', rowCfg)
      // console.log('paramRecord', paramRecord)
      // console.log('parentRow', parentRow)
      for (let i = 0; i < rowColumns.length; i += 1) {
        const col = rowColumns[i]
        if (col && col.qryColumn) {
          const cell = record[col.qryColumn.entitysid]
          if (!cell) {
            console.warn('Cell not found for entitysid', col.qryColumn.entitysid, 'in record', record)
          }
          cells.push({
            title: cell ? (cell.title != null ? cell.title : cell.value) : null,
            value: cell ? cell.value : null,
            column: col,
          })
        } else if (col.cfgColumn && (col.cfgColumn.type === 'scalar' || col.cfgColumn.type === 'aggr_sum')) {
          let param = null
          if (col.cfgColumn.type === 'scalar') {
            param = {
              name: `index_${i}`,
              method: 'scalarvalue',
              mode: 'sync',
              param: {
                scalarid: col.cfgColumn.scalarid || null,
                record: paramRecord || null,
              },
            }
          } else if (col.cfgColumn.type === 'aggr_sum') {
            /*
            const formRow = this.formParam.rows[this.formParam.rows.length - 1]
            const conditions = rowCfg.results.map(r => ({
              isactive: true,
              oper: { sid: rowCfg.mode === 'tree' ? 'inbranch' : 'equal' },
              args: [
                { type: { sid: 'entity' }, value: r.destination_entityid },
                { type: { sid: 'value' }, value: record[this.entities[r.source_entityid]?.entity.sid]?.value || null },
              ],
            }))
            // console.log('conditions', conditions)

            const condsExlcEqual = []
            for (let k = 0; k < formRow.filter.node.conds.length; k += 1) {
              const cond = formRow.filter.node.conds[k]
              let crit = true
              for (let l = 0; l < rowCfg.results.length; l += 1) {
                const r = rowCfg.results[l]
                if (cond.oper.sid === 'equal' && cond.args[0].type.sid === 'entity' && cond.args[0].value === r.destination_entityid) {
                  crit = false
                  break
                }
              }
              if (crit) condsExlcEqual.push(cond)
            }
            const node = {
              isactive: true,
              oper: formRow.filter.node.oper,
              nodes: formRow.filter.node.nodes || [],
              conds: condsExlcEqual,
            }
            const flt = {
              isactive: true,
              node: {
                isactive: true,
                oper: { sid: 'and' },
                nodes: [
                  node,
                ],
                conds: conditions,
              },
            }
             */

            const flt = this.getFilterForAggrSum(rowCfg, record)

            const grp = {
              isactive: true,
              groups: [
                {
                  isactive: true,
                  oper: { sid: 'sum' },
                  entity: { value: col.cfgColumn.destination_entityid },
                },
              ],
            }
            param = {
              name: `index_${i}`,
              method: 'query',
              mode: 'sync',
              param: {
                objectid: this.formParam.rows[this.formParam.rows.length - 1].objectid,
                filter: this.withDelFilter(this.formParam.rows[this.formParam.rows.length - 1].objectid, flt),
                groupby: grp,
                limit: 1,
                paramrecord: { record: paramRecord || null, keymode: 'sid' },
                keymode: 'sid',
              },
            }
          }

          multiParams.push(param)
          cells.push({
            title: null,
            value: null,
            column: col,
            mode: rowCfg.mode || 'list',
          })
        } else {
          cells.push({
            title: null,
            value: null,
            column: col,
            mode: rowCfg.mode || 'list',
          })
        }
      }

      if (multiParams.length > 0) {
        const multi = await useCubus.loadMultiQuery(this, { requests: multiParams })
        // console.log('multi', multi)
        const multiResults = multi.result.results
        cells.forEach((cell, cellIndex) => {
          const sclrRes = multiResults.find(r => r.name === `index_${cellIndex}`)
          if (sclrRes) {
            const sclr = sclrRes.result
            if (sclr && sclr.query) {
              const qRec = sclr.query.recordset.records[0]
              const qCell = qRec[this.entities[cell.column.cfgColumn.destination_entityid].entity.sid]
              if (!qCell) {
                console.log('destination_entityid', cell.column.cfgColumn.destination_entityid)
              }
              cells[cellIndex] = {
                title: qCell?.title,
                value: qCell?.value,
                column: cell.column,
                mode: rowCfg.mode || 'list',
                scalarvalue: sclr,
              }
            } else {
              cells[cellIndex] = {
                title: null,
                value: sclr.cell.value,
                column: cell.column,
                mode: rowCfg.mode || 'list',
                scalarvalue: sclr,
              }
            }
          }
        })
      }

      return cells
    },
    getRowRecord(parentRow, row, cfg) {
      let rec = parentRow ? JSON.parse(JSON.stringify(parentRow.record)) : JSON.parse(JSON.stringify(this.record.record.record))
      if (cfg.mode === 'record' || cfg.mode === 'tree_record') rec = row
      this.mapResultsToRecord(row, cfg, rec)
      return rec
    },

    cellType(row, col) {
      // console.log('cellType', row, col)
      if (row.level === this.maxRowLevel() && col.level === this.maxColumnLevel() && !row.isFolder) return 'value'
      return 'total'
    },
    formatNumber(value) {
      // BUG-784: numeric приходит строкой («420000.00») — раньше она шла на экран как есть.
      const num = typeof value === 'number' ? value : this.numericString(value)
      if (num == null) return value
      if (num === 0) return '-'
      if (!Number.isInteger(num)) return num.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
      return num.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    },
    numericString(value) {
      if (typeof value !== 'string' || !/^\s*-?\d+([.,]\d+)?\s*$/.test(value)) return null
      return this.toNumber(value)
    },
    getCellRecord(row, col, load = true) {
      const r = this.valueRecords.find(rec => this.checkRecord(rec, row, col))
      if (r) {
        return r
      }
      const newRecord = this.getNewRecord(this.paramRecord(row, col))
      this.valueRecords.push(newRecord)
      if (!load) return newRecord
      this.loadRow(row)
      this.loadCellRecord(row, col)
      return newRecord
    },
    // id/sid/del are never part of a cell's identity: the row/col reference records carry
    // them as nulls (copied from the header record), while a loaded cell record has real
    // ones — comparing them would mark every loaded cell as "not matching" and re-create
    // placeholders (and queries) forever. Identity is the business keys only.
    isServiceKey(key) {
      return key === 'id' || key === 'sid' || key === 'del'
    },
    checkRecord(record, row, col) {
      // eslint-disable-next-line no-restricted-syntax
      for (const key in row.record) {
        // eslint-disable-next-line no-prototype-builtins
        if (record.hasOwnProperty(key) && !this.isServiceKey(key)) {
          if (!this.cellValueMatches(record[key], row.record[key])) return false
        }
      }
      // eslint-disable-next-line no-restricted-syntax
      for (const key in col.record) {
        // eslint-disable-next-line no-prototype-builtins
        if (record.hasOwnProperty(key) && !this.isServiceKey(key)) {
          if (!this.cellValueMatches(record[key], col.record[key])) return false
        }
      }
      return true
    },
    async loadCellRecord(row, col) {
      const pr = this.paramRecord(row, col)
      // console.log('checkRecord.paramRecord', pr)
      const qry = await this.loadQuery(this, {
        keymode: 'sid',
        objectid: this.formParam.value_cell.objectid,
        filter: this.cellValueFilter(row, col),
        groupby: this.formParam.value_cell.groupby,
        paramrecord: { keymode: 'sid', record: pr },
        limit: 1,
      })
      if (qry) this.initRefs(qry)
      if (qry && qry.recordset && qry.recordset.records.length > 0) {
        const loadedRecord = qry.recordset.records[0]
        const initRecord = this.valueRecords.find(rec => this.checkRecord(rec, row, col))
        if (initRecord) {
          Object.keys(loadedRecord).forEach(key => {
            initRecord[key].value = loadedRecord[key].value
            initRecord[key].title = loadedRecord[key].title
          })
          initRecord.status = 'loaded'
        } else {
          this.valueRecords.push(loadedRecord)
        }
      } else {
        const initRecord = this.valueRecords.find(rec => this.checkRecord(rec, row, col))
        if (initRecord) {
          initRecord.status = 'empty'
        } else {
          const newRecord = this.getNewRecord(this.paramRecord(row, col))
          newRecord.status = 'empty'
          this.valueRecords.push(newRecord)
        }
      }
    },
    async loadRow(row) {
      const r = row
      if (row.cbs_loadingStarted) return
      r.cbs_loadingStarted = true
      const qry = await this.loadQuery(this, {
        keymode: 'sid',
        objectid: this.formParam.value_cell.objectid,
        filter: this.rowValueFilter(row),
        paramrecord: { keymode: 'sid', record: this.paramRow(row) },
      })

      const columns = this.getColumns()

      if (qry && qry.recordset && qry.recordset.records.length > 0) {
        qry.recordset.records.forEach(loadedRecord => {
          columns.forEach(col => {
            const existingRecord = this.valueRecords.find(rec => this.checkRecord(rec, row, col))
            if (!existingRecord || existingRecord.status !== 'loading') return
            let matches = true
            // eslint-disable-next-line no-restricted-syntax
            for (const key in row.record) {
              // eslint-disable-next-line no-prototype-builtins
              if (loadedRecord.hasOwnProperty(key) && !this.isServiceKey(key) && !this.cellValueMatches(loadedRecord[key], row.record[key])) {
                matches = false
                break
              }
            }
            if (matches) {
              // eslint-disable-next-line no-restricted-syntax
              for (const key in col.record) {
                // eslint-disable-next-line no-prototype-builtins
                if (loadedRecord.hasOwnProperty(key) && !this.isServiceKey(key) && !this.cellValueMatches(loadedRecord[key], col.record[key])) {
                  matches = false
                  break
                }
              }
            }

            if (matches) {
              Object.keys(loadedRecord).forEach(key => {
                if (existingRecord[key]) {
                  existingRecord[key].value = loadedRecord[key].value
                  existingRecord[key].title = loadedRecord[key].title
                }
              })
              existingRecord.status = 'loaded'
            }
          })
        })
        columns.forEach(col => {
          const existingRecord = this.valueRecords.find(rec => this.checkRecord(rec, row, col))
          if (existingRecord && existingRecord.status === 'loading') {
            existingRecord.status = 'empty'
          }
        })
      } else {
        columns.forEach(col => {
          const existingRecord = this.valueRecords.find(rec => this.checkRecord(rec, row, col))
          if (existingRecord && existingRecord.status === 'loading') {
            existingRecord.status = 'empty'
          }
        })
      }
    },

    copyLine() {
      if (!this.showCopyButton || !this.selectedCell) return
      const srcRow = this.getRows()[this.selectedCell.rx]

      // куда вставляем — туда же, куда вставлял бы плюс
      if (srcRow.cfg.mode === 'record' || srcRow.cfg.mode === 'tree_record') {
        this.copySiblingLine(srcRow)
      } else {
        // на всякий — если вдруг будет нужно копировать в режим list (не record)
        this.copySubLine(srcRow)
      }
      this.recalculateZIndices()
    },

    copySiblingLine(srcRow) {
      const parentRow = srcRow.parentRow || null
      const newRow = this.makeClonedRow(srcRow, parentRow)

      if (parentRow) {
        const at = parentRow.subs.indexOf(srcRow) + 1
        parentRow.subs.splice(at, 0, newRow)
      } else {
        const at = this.rows.indexOf(srcRow) + 1
        this.rows.splice(at, 0, newRow)
      }

      // сохраняем сразу
      this.saveRowRecord(newRow)

      // выбрать вставленную копию
      this.$nextTick(() => {
        const newRx = this.getRows().indexOf(newRow)
        this.selectedCell = { rx: newRx, ii: 0, i: -1 }
        this.updateShowPlusButton(newRow)
        this.updateShowDeleteButton(newRow)
        this.updateShowCopyButton(newRow)
        this.$nextTick(() => {
          this.ensureSelectedVisible()
          this.calculatePlusButtonPosition()
          this.calculateDeleteButtonPosition()
          this.calculateCopyButtonPosition()
        })
      })
    },

    copySubLine(srcRow) {
      // fallback: вставка копии в children
      if (!srcRow.isExpanded) this.onClickRowExpand(srcRow, 'row')
      const newRow = this.makeClonedRow(srcRow, srcRow)
      srcRow.subs.splice(0, 0, newRow)
      this.saveRowRecord(newRow)
    },

    makeClonedRow(srcRow, parentRow) {
      const parentLevel = parentRow ? parentRow.level : 0
      const subLevel = parentRow ? (parentRow.isFolder ? parentRow.level : parentRow.level + 1) : 1
      const subFolderLevel = parentRow ? parentRow.level_with_folders + 1 : 1

      const { cfg } = srcRow
      // создаём заготовку как для новой строки
      const rec = this.getNewEditableRecord(cfg.objectid, parentRow)

      // переносим значения из исходной строки, кроме id
      Object.keys(srcRow.record).forEach(key => {
        if (key === 'id') return
        const cell = srcRow.record[key]
        if (!cell || typeof cell !== 'object') return
        if (!Object.hasOwn(rec, key)) return
        rec[key] = { value: cell.value, title: cell.title }
      })

      // id в любом случае пустой
      if (Object.hasOwn(rec, 'id')) {
        rec.id = { value: null, title: null }
      }

      // пересобираем видимые ячейки по этому record
      const cells = this.getCellsForEditableRecord(cfg, rec)

      return {
        level: subLevel,
        level_with_folders: subFolderLevel,
        isExpanded: false,
        record: rec,
        cells,
        subs: [],
        status: 'new',
        subStatus: 'empty',
        isFolder: false,
        parent: parentRow,
        id: null,
        addrecord: cfg.addrecord || false,
        mode: cfg.mode || 'record',
        cfg,
        parentRow: parentRow || null,
        zIndex: 1,
      }
    },
    paramRecord(row, col) {
      // console.log('paramRecord.row', row)
      // console.log('paramRecord.col', col)
      // const n = {}
      const n = JSON.parse(JSON.stringify(this.record.record.record))
      Object.keys(col.record).forEach(key => {
        n[key] = { value: col.record[key].value, title: col.record[key].title, mode: col.record[key].mode || 'list' }
      })
      Object.keys(row.record).forEach(key => {
        n[key] = { value: row.record[key].value, title: row.record[key].title, mode: row.record[key].mode || 'list' }
      })
      // console.log('n', n)
      return n
    },
    paramRow(row) {
      const n = {}
      Object.keys(row.record).forEach(key => {
        n[key] = { value: row.record[key].value, title: row.record[key].title }
      })
      return n
    },
    cellValueFilter(row, col) {
      const rowConds = this.getConditions(row.record)
      const rowNode = {
        isactive: true,
        oper: { sid: 'and' },
        nodes: [],
        conds: rowConds,
      }
      const colConds = this.getConditions(col.record)
      const colNode = {
        isactive: true,
        oper: { sid: 'and' },
        nodes: [],
        conds: colConds,
      }
      const nodes = []
      nodes.push(rowNode)
      nodes.push(colNode)
      if (this.formParam.value_cell.filter) {
        nodes.push(this.formParam.value_cell.filter.node)
      }
      return {
        isActive: true,
        node: {
          isactive: true,
          oper: { sid: 'and' },
          nodes,
          conds: [],
        },
      }
    },
    rowValueFilter(row) {
      const rowConds = this.getConditions(row.record)
      const rowNode = {
        isactive: true,
        oper: { sid: 'and' },
        nodes: [],
        conds: rowConds,
      }
      const nodes = []
      nodes.push(rowNode)
      const drNode = this.daterangeFilterNode()
      if (drNode) nodes.push(drNode)
      if (this.formParam.value_cell.filter) {
        nodes.push(this.formParam.value_cell.filter.node)
      }
      return {
        isActive: true,
        node: {
          isactive: true,
          oper: { sid: 'and' },
          nodes,
          conds: [],
        },
      }
    },
    getConditions(rec) {
      const conds = []
      Object.keys(rec).forEach(key => {
        if (this.checkValueObjAttribute(key)) {
          const cell = rec[key]
          conds.push({
            isactive: true,
            // eslint-disable-next-line no-nested-ternary
            oper: { sid: rec[key].mode && (rec[key].mode === 'tree' || rec[key].mode === 'tree_record') ? 'inbranch' : (rec[key].mode === 'day' ? 'inday' : 'equal') },
            args: [
              { type: { sid: 'entitysid' }, value: key },
              { type: { sid: 'value' }, value: cell.value },
            ],
          })
        }
      })
      return conds
    },
    async initValueObj() {
      this.valueObj = await useCubus.loadObject(this, {
        objectid: this.formParam.value_cell.objectid,
      })
    },
    checkValueObjAttribute(entitySid) {
      return this.valueObj.attributes.some(attr => attr.entitysid === entitySid)
    },
    getNewRecord(rec) {
      const newRec = {}
      this.valueObj.attributes.forEach(attr => {
        newRec[attr.entitysid] = {
          value: attr.default.value,
          title: attr.default.title,
        }
      })
      // Fill auto-computed fields
      if (newRec.createdat && !newRec.createdat.value) {
        newRec.createdat = { value: new Date().toISOString(), title: null }
      }
      if (newRec.authorsid && !newRec.authorsid.value) {
        const userSid = this.$store?.getters['cubus-store/SESSION']?.user?.sid
        if (userSid) newRec.authorsid = { value: userSid, title: null }
      }
      Object.keys(rec).forEach(key => {
        if (this.checkValueObjAttribute(key)) {
          newRec[key] = { value: rec[key].value, title: rec[key].title }
        }
      })
      // BUG-018: a cell's value may only come from its own loaded record or user input.
      // Attribute defaults and header/row copies otherwise render phantom values
      // (checked ticks, numbers) in cells that have no record.
      this.valueEntities.forEach(ve => {
        newRec[ve.entity.sid] = { value: null, title: null }
      })
      newRec.status = 'loading'
      return newRec
    },
    /*
    async initValueEntity() {
      this.valueEntity = await useCubus.loadEntity(this, {
        entityid: this.formParam.value_cell.result.entityid,
      })
    },
    */
    initValueEntities() {
      if (this.formParam.value_cell && this.formParam.value_cell.results) {
        this.formParam.value_cell.results.forEach(attr => {
          const ent = this.entities[attr.entityid]
          if (ent && ent.entity) {
            this.valueEntities.push({ editable: attr.editable, entity: ent.entity })
          }
        })
      }
    },
    // REQ-004 p.2 (per-row render): value_cell.results[i].render_source =
    // {objectid, key_entityid, render_entityid, checkbox_value} — the row key (e.g. habit id)
    // is looked up in the source catalog and the cell renders as a checkbox only when the
    // row's render entity (e.g. tracker type) equals checkbox_value.
    async initRenderSources() {
      const results = (this.formParam.value_cell && this.formParam.value_cell.results) || []
      await Promise.all(results.filter(r => r.render_source).map(async r => {
        const rs = r.render_source
        const qry = await this.loadQuery(this, {
          keymode: 'sid',
          objectid: Number(rs.objectid),
          limit: 10000,
        })
        if (!qry || !qry.recordset) return
        const renderCol = qry.columns.find(c => c.entityid === Number(rs.render_entityid))
        if (!renderCol) return
        const map = {}
        qry.recordset.records.forEach(rec => {
          const idCell = rec.id
          const renderCell = rec[renderCol.entitysid]
          if (idCell && renderCell) map[idCell.value] = renderCell.value
        })
        this.$set(this.renderSourceMaps, Number(r.entityid), map)
      }))
    },
    cellRenderType(row, ve) {
      const res = ((this.formParam.value_cell && this.formParam.value_cell.results) || [])
        .find(r => Number(r.entityid) === ve.entity.id)
      const rs = res && res.render_source
      if (rs) {
        const keyEnt = this.entities[Number(rs.key_entityid)]
        const keySid = keyEnt && keyEnt.entity ? keyEnt.entity.sid : null
        const keyCell = keySid && row.record ? row.record[keySid] : null
        const map = this.renderSourceMaps[ve.entity.id]
        if (keyCell && map && map[keyCell.value] !== undefined) {
          return Number(map[keyCell.value]) === Number(rs.checkbox_value) ? 'checkbox' : ''
        }
        return ''
      }
      return ve.entity.rendertype || ''
    },
    onUpdateRecord(row, col, valueEntity, newValue, newTitle) {
      const record = this.getCellRecord(row, col, 'update')
      const r = record
      const cell = r[valueEntity.entity.sid]

      if (!(cell.value == null && Number(newValue) === 0) && String(cell.value) === String(newValue)) {
        return
      }

      // запоминаем значение ДО перезаписи — для одношаговой отмены (Ctrl+Z)
      const _oldValue = cell.value
      const _oldTitle = cell.title

      r.status = 'locked'
      cell.value = newValue
      cell.title = newTitle
      cell.status = 'changed'

      const isPivotMatrix = Array.isArray(this.formParam?.columns) && this.formParam.columns.length > 0

      this.lastEdit = {
        mode: isPivotMatrix ? 'matrix' : 'row', cell, record: r, row, col,
        from: { value: _oldValue, title: _oldTitle },
        to: { value: newValue, title: newTitle },
      }

      if (isPivotMatrix) {
        this.saveRecord(row, col, r)
      } else {
        // обычный режим по строкам → сохраняем при уходе со строки
        const rows = this.getRows?.() || []
        const realRow = rows[row.rx ?? row.__rx ?? row.index ?? row]
        if (realRow) {
          this.markRowDirty(realRow)
        }
      }
    },

    // Одношаговая отмена последней правки ячейки: вернуть прежнее значение и ре-сохранить.
    // Повторный Ctrl+Z работает как «вернуть» (redo) — from/to меняются местами.
    undoLastEdit() {
      const le = this.lastEdit
      if (!le || !le.cell) return
      const { cell } = le
      this.$set(cell, 'value', le.from.value)
      this.$set(cell, 'title', le.from.title)
      cell.status = 'changed'
      if (le.record) le.record.status = 'locked'
      if (le.mode === 'matrix') {
        this.saveRecord(le.row, le.col, le.record)
      } else {
        this.markRowDirty(le.row)
        this.saveRowRecord(le.row).then(ok => { if (ok) this.clearRowDirty(le.row) })
      }
      // переключаем направление, чтобы следующий Ctrl+Z вернул значение обратно
      this.lastEdit = { ...le, from: le.to, to: le.from }
      try { useCubus.toastMessage(this, 'Отменено') } catch (e) { /* no-op */ }
    },
    onUpdateRowRecord(row, entitysid, newValue, newTitle) {
      if (!(this._dirtyRows instanceof Set)) this._dirtyRows = new Set()
      const key = row?.id || row?.record?.id || row?.rx
      if (key) this._dirtyRows.add(key)
      const r = row.record
      const cell = r[entitysid]

      const oldVal = cell?.value ?? null
      const oldTitle = cell?.title ?? null
      const nextVal = newValue ?? null
      const nextTitle = newTitle ?? null

      const changed = (oldVal !== nextVal) || (oldTitle !== nextTitle)
      if (!changed) return

      // Remember the last known-saved value on first edit so we can roll back if save fails.
      if (cell && cell.status !== 'changed' && !Object.hasOwn(cell, '_lastSavedValue')) {
        this.$set(cell, '_lastSavedValue', oldVal)
        this.$set(cell, '_lastSavedTitle', oldTitle)
      }

      this.$set(cell, 'value', nextVal)
      this.$set(cell, 'title', nextTitle)
      cell.status = 'changed'

      this.lastEdit = {
        mode: 'row', cell, record: r, row, entitysid,
        from: { value: oldVal, title: oldTitle },
        to: { value: nextVal, title: nextTitle },
      }

      this.markRowDirty(row)
      this.maybeContinueDeepAdd(row)
    },
    maybeContinueDeepAdd(row) {
      const { cfg } = row
      if (!cfg) return
      const pairs = cfg.results || (cfg.result ? [cfg.result] : [])
      const requiredSids = pairs
        .map(p => this.entities?.[p.destination_entityid]?.entity?.sid)
        .filter(Boolean)

      const allFilled = requiredSids.every(sid => row.record?.[sid]?.value != null)

      if (!allFilled) return

      // если ещё есть глубже — раскрываем и создаём следующий уровень
      if (row.level < this.maxRowLevel()) {
        this.addDeepRow(row) // продолжение от текущего заполненного уровня
      }
    },

    async saveRowRecord(row) {
      console.log('saveRowRecord')
      // Front-level required check (form_param.columns[].required) — backend only knows about catalog-attribute `required`.
      const missing = this.getMissingRequiredFields(row)
      if (missing.length > 0) {
        const list = missing.map(n => `• ${n}`).join('\n')
        await this.$bvModal.msgBoxOk(list, {
          title: 'Заполните обязательные поля',
          okVariant: 'warning',
          centered: true,
          headerBgVariant: 'warning',
        })
        return false
      }
      const r = row.record
      // Snapshot current cell values so we can roll back if the backend rejects the save
      // (object_constraint / required / unique). Without this, the cell keeps the user's
      // invalid input but the DB still has the old value — UI ends up out of sync.
      const snapshot = this.snapshotRecord(r)
      if (!Object.hasOwn(r, 'status')) this.$set(r, 'status', 'locked')
      else r.status = 'locked'
      try {
        // Call backend directly so we can read server-side validation errors (required/unique/constraints)
        // and show them in a modal instead of the small toast that useCubus.saveRecord uses by default.
        const useJwt = require('@/cubus/jwt/useJwt').default
        const response = await useJwt.query({
          token: useJwt.getCubusToken(),
          query: {
            method: 'saverecord',
            mode: 'sync',
            param: {
              keymode: 'sid',
              objectid: row.cfg.objectid,
              record: row.record,
              // CUB-1310: запись через форму — сервер проверит право и на саму форму
              formid: this.objectFull.object.id,
            },
          },
        })
        const data = response?.data || {}
        if (data.error) {
          this.restoreRecordFromSnapshot(r, snapshot)
          await this.$bvModal.msgBoxOk(useCubus.accessErrorText(data.error, this.srcNames), {
            title: 'Не удалось сохранить запись',
            okVariant: 'danger',
            centered: true,
            headerBgVariant: 'danger',
          })
          return false
        }
        const saved = data?.result?.record
        if (!saved) {
          this.restoreRecordFromSnapshot(r, snapshot)
          await this.$bvModal.msgBoxOk('Сервер вернул пустой ответ.', {
            title: 'Не удалось сохранить запись',
            okVariant: 'danger',
            centered: true,
          })
          return false
        }
        this.afterSaveRowRecord(row, saved)
        return true
      } catch (e) {
        console.error(e)
        this.restoreRecordFromSnapshot(r, snapshot)
        await this.$bvModal.msgBoxOk(useCubus.accessErrorText(e?.response?.data?.error || e?.message || e, this.srcNames), {
          title: 'Ошибка сохранения',
          okVariant: 'danger',
          centered: true,
        })
        return false
      }
    },
    snapshotRecord() { return null /* unused — rollback uses cell._lastSavedValue */ },
    restoreRecordFromSnapshot(record) {
      // Roll back any cell that was dirty to its last-saved value (captured in onUpdateRowRecord).
      // For brand-new records (no id) we keep the user's input so they can fix and retry —
      // rolling back to null would just wipe what they typed.
      if (!record) return
      const isNew = !record.id || record.id.value == null || record.id.value === 0
      if (isNew) {
        if (Object.hasOwn(record, 'status')) record.status = 'changed'
        return
      }
      Object.keys(record).forEach(key => {
        const cell = record[key]
        if (cell && typeof cell === 'object' && cell.status === 'changed' && Object.hasOwn(cell, '_lastSavedValue')) {
          this.$set(cell, 'value', cell._lastSavedValue)
          this.$set(cell, 'title', cell._lastSavedTitle)
          cell.status = 'saved'
          delete cell._lastSavedValue
          delete cell._lastSavedTitle
        }
      })
      if (Object.hasOwn(record, 'status')) record.status = 'saved'
    },
    afterSaveRowRecord(row, savedRec) {
      console.log('afterSaveRowRecord ', row, savedRec)
      const i = row.record
      const s = savedRec

      // eslint-disable-next-line no-restricted-syntax
      for (const key in savedRec) {
        // eslint-disable-next-line no-prototype-builtins
        if (key !== 'actions') {
          // eslint-disable-next-line no-prototype-builtins
          if (i.hasOwnProperty(key)) {
            // Save succeeded — update cell from server, mark clean, drop rollback breadcrumb.
            i[key].value = savedRec[key].value
            i[key].title = savedRec[key].title
            if (Object.hasOwn(i[key], 'status')) i[key].status = 'saved'
            if (Object.hasOwn(i[key], '_lastSavedValue')) delete i[key]._lastSavedValue
            if (Object.hasOwn(i[key], '_lastSavedTitle')) delete i[key]._lastSavedTitle
          } else {
            this.$set(i, key, {
              value: savedRec[key].value,
              title: savedRec[key].title,
            })
          }
        }
      }
      s.status = 'saved'
      i.status = 'saved'
      const r = row
      r.status = 'changed'
      r.lastChange = useCubus.guid()
      this.recalcTable()
    },

    async saveRecord(row, col, record) {
      // console.log('saveRecord', record)
      try {
        const saved = await useCubus.saveRecord(this, {
          keymode: 'sid',
          objectid: this.formParam.value_cell.objectid,
          record,
          formid: this.objectFull.object.id,
        })
        this.afterSave(record, saved, row, col)
      } catch (e) {
        console.error(e)
        record.status = 'error'
      }
    },
    afterSave(initRec, savedRec, row, col) {
      // console.log('afterSave', initRec, savedRec, row, col)
      const i = initRec
      const s = savedRec

      // eslint-disable-next-line no-restricted-syntax
      for (const key in savedRec) {
        // eslint-disable-next-line no-prototype-builtins
        if (savedRec.hasOwnProperty(key)) {
          if (key !== 'actions') {
            // eslint-disable-next-line no-prototype-builtins
            if (i.hasOwnProperty(key)) {
              // Don't overwrite cells that were changed while save was in-flight
              if (i[key].status === 'changed') continue
              i[key].value = savedRec[key].value
              i[key].title = savedRec[key].title
            } else {
              this.$set(i, key, {
                value: savedRec[key].value,
                title: savedRec[key].title,
              })
            }
          }
        }
      }
      s.status = 'saved'
      i.status = 'saved'
      const r = row
      const c = col
      r.status = 'changed'
      c.status = 'changed'
      const lastChange = useCubus.guid()
      r.lastChange = lastChange
      c.lastChange = lastChange
      // if (this.formParam.total_cell)
      this.recalcTable()
    },

    getTotalRecord(row, col, load = true) {
      const r = this.totalRecords.find(rec => this.checkTotalRecord(rec, row, col))
      if (r) return r
      const newRecord = this.getNewTotalRecord(row, col)
      this.totalRecords.push(newRecord)
      if (!load) return newRecord
      this.loadTotalCellRecord(row, col, row.lastChange || col.lastChange)
      return newRecord
    },
    async loadTotalCellRecord(row, col, currentChange) {
      const cfg = this.formParam.total_cell
      const qry = await this.loadQuery(this, {
        keymode: 'sid',
        objectid: cfg.objectid,
        filter: this.totalCellFilter(row, col),
        groupby: cfg.groupby,
        paramrecord: { keymode: 'sid', record: this.paramRecord(row, col) },
        limit: 1,
      })
      if (qry && qry.recordset && qry.recordset.records.length > 0) {
        const loadedRecord = qry.recordset.records[0]
        const initRecord = this.totalRecords.find(rec => this.checkTotalRecord(rec, row, col))
        if (initRecord) {
          this.totalEntities.forEach(ve => {
            const key = ve.entity.sid
            initRecord.record[key].value = loadedRecord[key].value
            initRecord.record[key].title = loadedRecord[key].title
          })
          initRecord.status = 'loaded'
        } else {
          this.totalRecords.push({
            record: loadedRecord,
            row,
            col,
            status: 'loaded',
          })
        }
      } else {
        const initRecord = this.totalRecords.find(rec => this.checkTotalRecord(rec, row, col))
        if (initRecord) {
          initRecord.status = 'empty'
        } else {
          const newRecord = this.getNewTotalRecord(row, col)
          newRecord.status = 'empty'
          this.totalRecords.push(newRecord)
        }
      }
      if (currentChange) this.checkLastChange(row, col, currentChange)
    },
    async initTotalObj() {
      this.totalObj = await useCubus.loadObject(this, {
        objectid: this.formParam.total_cell.objectid,
      })
    },
    initTotalEntities() {
      if (this.formParam.total_cell && this.formParam.total_cell.results) {
        this.formParam.total_cell.results.forEach(attr => {
          const ent = this.entities[attr.entityid]
          if (ent && ent.entity) {
            this.totalEntities.push({ editable: false, entity: ent.entity })
          }
        })
      }
    },
    checkTotalRecord(record, row, col) {
      return record.row === row && record.col === col
    },
    getNewTotalRecord(row, col) {
      const rec = this.paramRecord(row, col)
      const newRec = JSON.parse(JSON.stringify(rec))
      this.totalEntities.forEach(ve => {
        newRec[ve.entity.sid] = { value: null, title: null }
      })
      return {
        record: newRec,
        status: 'loading',
        row,
        col,
      }
    },
    totalCellFilter(row, col) {
      const prmRec = this.paramRecord(row, col)
      const conds = this.getConditions(prmRec)
      const prmNode = {
        isactive: true,
        oper: { sid: 'and' },
        nodes: [],
        conds,
      }
      const nodes = []
      nodes.push(prmNode)
      if (this.formParam.total_cell.filter) nodes.push(this.formParam.total_cell.filter.node)
      return {
        isactive: true,
        node: {
          isactive: true,
          oper: { sid: 'and' },
          nodes,
          conds: [],
        },
      }
    },
    isCellLocked(row, col) {
      const rec = this.valueRecords.find(r => this.checkRecord(r, row, col))
      return rec && rec.status === 'locked'
    },
    isEditable(row, col, i = null, ii = null) {
      if (row.isFolder) return false
      if (i >= 0) {
        if (this.cellType(row, col) !== 'value') return false
      } else if (col === undefined) {
        const rowCell = row.cells[ii]
        if (!rowCell || !rowCell.column || !rowCell.column.cfgColumn) return false
        if (!rowCell.column.cfgColumn.editable) return false
        return this.canEditRow(row)
      }
      return this.canDo('update', this.formParam.value_cell?.objectid)
    },
    valueCellStyle(row, col, ve) {
      if (this.isCellLocked(row, col)) {
        return { 'background-color': '#f0f0f0', opacity: '0.7', 'pointer-events': 'none' }
      }
      if (this.isEditable(row, col) && !!ve.editable) {
        if (ve.entity.datatype.sid === 'ref') {
          return { 'background-color': '#E8F2FF' }
        }
        return { 'background-color': 'lightyellow' }
      }
      return {}
    },
    totalCellValue(row, col, ve) {
      const rec = this.getTotalRecord(row, col)
      if (rec) {
        const cell = rec.record[ve.entity.sid]
        if (cell) {
          if (cell.value) {
            if (cell.value !== 0) {
              return cell.value
            }
            return '-'
          }
        }
      }
      return null
    },

    async recalcTable() {
      this.rows.forEach(row => this.checkStatusRow(row))
      this.columns.forEach(row => this.checkStatusRow(row))
      await this.refreshRows(null, this.rows)
      this.recalcTotals()
    },
    recalcTotals() {
      this.totalRecords.forEach(rec => this.recalcTotalRecord(rec))
    },
    recalcTotalRecord(rec) {
      if (rec.row.status === 'changed' || rec.col.status === 'changed') {
        const r = rec
        r.status = 'loading'
        // console.log('r', r)
        this.loadTotalCellRecord(r.row, r.col, r.row.lastChange || r.col.lastChange)
      }
    },
    checkStatusRow(row) {
      const r = row
      for (let i = 0; i < r.subs.length; i += 1) {
        if (this.checkStatusRow(r.subs[i]) === 'changed') {
          // console.log('checkStatusRow', r)
          r.status = 'changed'
        }
      }
      return r.status
    },
    async refreshRows(parentRow, rows) {
      if (rows.length === 0) return
      if (rows.every(r => r.status === 'ready')) return
      const { cfg } = rows[0]

      const qry = parentRow ? parentRow.subsQuery : this.getQuery('row_1')
      const rowColumns = this.getRowColumns(cfg, qry)
      const matched = new Set()
      for (let i = 0; i < qry.recordset.records.length; i += 1) {
        const rec = qry.recordset.records[i]
        // eslint-disable-next-line no-await-in-loop
        const rowRecord = await this.getRowRecord(parentRow, rec, cfg)
        const row = this.findRow(rows, rowRecord)
        if (row && row.status !== 'ready') {
          // eslint-disable-next-line no-await-in-loop
          row.cells = await this.getRowCells(rowColumns, rec, cfg, rowRecord, parentRow)
          matched.add(row)
        }
      }
      // Fallback: rows not in subsQuery (e.g. text-group rows created via + button
      // whose value didn't exist when query was loaded) — recompute using row's own record.
      for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i]
        if (row.status === 'ready' || matched.has(row)) continue
        // eslint-disable-next-line no-await-in-loop
        row.cells = await this.getRowCells(rowColumns, row.record, cfg, row.record, parentRow)
      }
      for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i]
        // eslint-disable-next-line no-await-in-loop
        await this.refreshRows(row, row.subs)
      }
    },
    findRow(rows, record) {
      for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i]
        if (this.checkRowRecord(row, record) === true) {
          return row
        }
      }
      return null
    },
    checkRowRecord(row, record) {
      // eslint-disable-next-line no-restricted-syntax
      for (const key of Object.keys(row.record)) {
        if (Object.hasOwn(record, key)) {
          if (row.record[key].value === null && record[key].value !== null) return false
          if (row.record[key].value !== null && record[key].value === null) return false
          if (row.record[key].value !== null && record[key].value !== null && row.record[key].value !== record[key].value) return false
        }
      }
      return true
    },

    cellAccess(record, valueEntity) {
      return (record.status !== 'locked' && valueEntity.editable && this.canDo('update', this.formParam.value_cell?.objectid)) ? 'rw' : 'ro'
    },

    formatColumn(column) {
      // console.log(`column: ${JSON.stringify(column)}`)
      const cfgCols = this.formParam.columns[column.level - 1].columns
      if (!cfgCols || !cfgCols[0] || !cfgCols[0].format) return column.cells[0].title || column.cells[0].value
      const { format } = cfgCols[0]
      if (format === 'MMM YY') {
        const date = new Date(column.cells[0].title)

        if (Number.isNaN(date.getTime())) {
          console.warn('Invalid date:', column.cells[0].title)
          return column.cells[0].title
        }

        const formatted = date.toLocaleDateString('ru-RU', { month: 'short', year: '2-digit' })
        return formatted.replace(/\sг\.$/, '').replace(/\./, '')
      }
      return column.cells[0].title || column.cells[0].value
    },

    commitActiveEditor() {
      const el = document.activeElement
      if (el && el.contentEditable === 'true') {
        el.dispatchEvent(new Event('blur', { bubbles: true }))
        return true
      }
      // Also handle regular input fields (like in reference pickers)
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
        el.dispatchEvent(new Event('blur', { bubbles: true }))
        return true
      }
      return false
    },

    ensureSelectedVisible() {
      if (!this.selectedCell) return
      const { rx, ii, i } = this.selectedCell
      const ref = this.$refs[`td_${rx}_${ii}_${i}`]
      const cell = Array.isArray(ref) ? ref[0] : ref
      const scroller = this.$el.querySelector('.pivot-wrapper')
      if (!cell || !scroller) return

      const head = scroller.querySelector('thead')
      const headH = head ? head.getBoundingClientRect().height : 0

      const first = scroller.querySelector('.pivot-table th:first-child')
      const second = scroller.querySelector('.pivot-table th:nth-child(2)')
      const stickyLeft = (first?.getBoundingClientRect().width || 0) + (second?.getBoundingClientRect().width || 0)

      const s = scroller.getBoundingClientRect()
      const c = cell.getBoundingClientRect()

      if (c.top < s.top + headH) scroller.scrollTop -= (s.top + headH - c.top)
      else if (c.bottom > s.bottom) scroller.scrollTop += (c.bottom - s.bottom)

      if (c.left < s.left + stickyLeft) scroller.scrollLeft -= (s.left + stickyLeft - c.left)
      else if (c.right > s.right) scroller.scrollLeft += (c.right - s.right)
    },

    getRowPosition(target) {
      for (let i = 0; i < this.rows.length; i += 1) {
        const row = this.rows[i]
        const pos = this.findRowPosition(row, target)
        if (pos) return pos
      }
      return null
    },
    findRowPosition(parentRow, target) {
      if (parentRow.subs && parentRow.subs.length > 0) {
        for (let index = 0; index < parentRow.subs.length; index += 1) {
          const sub = parentRow.subs[index]
          if (sub === target) return { parent: parentRow, index }
          const subPos = this.findRowPosition(sub, target)
          if (subPos) return subPos
        }
      }
      return null
    },
    getRowParent(row) {
      const pos = this.getRowPosition(row)
      if (pos) return pos.parent
      return null
    },

    recalculateZIndices() {
      // z-index для строк должен быть меньше z-index заголовка (100)
      // Используем диапазон от 1 до 90
      const maxZIndex = 90
      let zIndex = Math.min(this.getRows().length + 10, maxZIndex)
      const recalc = subs => {
        subs.forEach(r => {
          r.zIndex = Math.max(zIndex, 1)
          zIndex -= 1
          if (r.subs && r.subs.length > 0 && r.isExpanded) recalc(r.subs)
        })
      }
      recalc(this.rows)
      this.calculatePlusButtonPosition()
      this.calculateDeleteButtonPosition()
      this.calculateCopyButtonPosition()
    },

    async newLine() {
      const row = this.selectedCell ? this.getRows()[this.selectedCell.rx] : null
      if (!row || !row.cfg) return

      const isLastLevel = row.level === this.maxRowLevel()

      if (isLastLevel) {
        if (row.cfg.mode === 'record') {
          this.newSiblingLine()
        } else {
          this.newSubLine()
        }
        this.recalculateZIndices()
        return
      }

      // For group-level rows (non-record mode), show a picker of available values
      const subCfg = this.getCfgForSubrows('row', row)
      if (subCfg && subCfg.mode !== 'record' && subCfg.mode !== 'tree_record') {
        await this.addGroupRow(row)
      } else {
        await this.addDeepRow(row)
      }
      this.recalculateZIndices()
    },

    async addGroupRow(parentRow, isRoot = false) {
      if (!isRoot && !parentRow.isExpanded) {
        this.onClickRowExpand(parentRow, 'row')
      }

      const cfg = isRoot ? this.formParam.rows[0] : this.getCfgForSubrows('row', parentRow)
      if (!cfg) return

      const pairs = cfg.results || (cfg.result ? [cfg.result] : [])
      if (pairs.length === 0) return

      // Determine if the destination entity is a ref type
      const destEntId = pairs[0].destination_entityid
      const destEnt = this.entities?.[destEntId]
      const isRef = destEnt?.entity?.datatype?.sid === 'ref'

      // Collect existing values in this group
      const existingSubs = isRoot ? this.rows : (parentRow.subs || [])
      const existingKeys = new Set()
      const destSids = pairs.map(p => this.entities?.[p.destination_entityid]?.entity?.sid).filter(Boolean)
      existingSubs.forEach(sub => {
        const key = destSids.map(sid => String(sub.record?.[sid]?.value ?? '')).join('|')
        existingKeys.add(key)
      })

      let available = []
      let qry = null

      if (isRef && destEnt.entity.refobjectid) {
        // Use reference() backend method — applies reference_availability rules (role/object/context)
        const multi = await useCubus.loadMultiQuery(this, {
          requests: [{
            name: 'ref',
            method: 'reference',
            mode: 'sync',
            param: {
              entity_sid: destEnt.entity.sid,
              destination_object_id: this.objectFull?.object?.id,
            },
          }],
        })
        const refList = multi?.result?.results?.[0]?.result?.reference
        if (!Array.isArray(refList)) return

        // reference returns flat list: { id, sid, name, code, parent, title, value }
        const parentIds = new Set()
        refList.forEach(r => {
          if (r.parent != null && Number(r.parent) !== 0) parentIds.add(String(r.parent))
        })
        // Normalize to records-like format the rest of the code expects
        const allRecs = refList.map(r => ({
          id: { value: r.id },
          sid: { value: r.sid },
          name: { value: r.name },
          code: { value: r.code },
          parent: { value: r.parent },
          isfolder: { value: parentIds.has(String(r.id)) },
        }))
        // Keep all records EXCEPT already-added ones; mark folders as disabled (greyed out)
        available = allRecs.filter(rec => !existingKeys.has(String(rec.id?.value ?? '')))

        if (available.length === 0) {
          await Swal.fire({
            title: 'Нет доступных значений',
            text: 'Все возможные значения уже добавлены в данную группу.',
            icon: 'info',
            confirmButtonText: 'OK',
          })
          return
        }

        // Build options — use name for display, with code if rendertype=codename.
        // Mark folder/parent records as disabled (greyed, not selectable).
        const rendertype = destEnt.entity.rendertype || 'name'
        const inputOptions = {}
        available.forEach((rec, idx) => {
          let display = rec.name?.value || `#${rec.id?.value || idx}`
          if (rendertype === 'codename' && rec.code?.value) {
            display = `${rec.code.value}. ${rec.name?.value || ''}`
          }
          const isFolder = rec.isfolder?.value === true || parentIds.has(String(rec.id?.value ?? ''))
          inputOptions[idx] = isFolder
            ? { label: display, disabled: true, hint: 'папка' }
            : display
        })

        const result = await this.showSearchablePicker('Выберите значение', inputOptions)
        if (result === null) return

        const selected = available[Number(result)]
        if (!selected) return

        // Build a synthetic query record that matches the expected structure for initSubRows/getRowRecord
        // Load the actual row query to get proper columns
        const qryParent = isRoot ? null : parentRow
        qry = await this.getRowQry(qryParent, cfg)

        // Build a synthetic record mapping the ref selection back into the query structure
        const srcSid = this.entities?.[pairs[0].source_entityid]?.entity?.sid
        const syntheticRecord = {}
        if (qry && qry.columns) {
          qry.columns.forEach(col => {
            syntheticRecord[col.entitysid] = { value: null, title: null }
          })
        }
        // Map selected reference id to the source entity
        if (srcSid) {
          syntheticRecord[srcSid] = { value: selected.id?.value, title: selected.name?.value }
        }
        // Copy name/id from reference
        syntheticRecord.id = { value: selected.id?.value, title: selected.id?.value }
        if (selected.name) syntheticRecord.name = { ...selected.name }

        // Create the row
        const rowColumns = this.getRowColumns(cfg, qry)
        const parentRec = isRoot ? null : parentRow
        const rec = this.getRowRecord(parentRec, syntheticRecord, cfg)
        const cells = await this.getRowCells(rowColumns, syntheticRecord, cfg, rec, parentRec)

        const newLevel = isRoot ? 1 : (parentRow.isFolder ? parentRow.level : parentRow.level + 1)
        const newLevelWithFolders = isRoot ? 1 : parentRow.level_with_folders + 1

        const newRow = {
          level: newLevel,
          level_with_folders: newLevelWithFolders,
          isExpanded: false,
          record: rec,
          cells,
          subs: [],
          status: 'ready',
          subStatus: 'empty',
          isFolder: false,
          parent: null,
          subParent: isRoot ? null : parentRow,
          id: selected.id?.value,
          addrecord: cfg.addrecord || false,
          mode: cfg.mode || 'list',
          cfg,
          zIndex: 1,
          parentRow: isRoot ? null : parentRow,
          subsQuery: null,
        }

        this.insertGroupRow(newRow, parentRow, isRoot)
      } else {
        // Non-ref: load from the groupby query as before
        const qryParent = isRoot ? null : parentRow
        qry = await this.getRowQry(qryParent, cfg)
        if (!qry || !qry.recordset || !qry.recordset.records) return

        const srcSids = pairs.map(p => this.entities?.[p.source_entityid]?.entity?.sid).filter(Boolean)

        // Detect TEXT-group: same objectid as child level + self-ref results on groupby entity
        const groupEntity = cfg.groupby?.groups?.[0]?.entity?.value
        const hasSelfRef = groupEntity && pairs.some(p => p.source_entityid === groupEntity && p.destination_entityid === groupEntity)
        const nextRow = this.formParam?.rows?.[this.formParam.rows.findIndex(r => r === cfg) + 1]
        const isTextGroup = hasSelfRef && nextRow && Number(nextRow.objectid) === Number(cfg.objectid)

        // Filter out existing (source values in query correspond to destination values in subs)
        available = qry.recordset.records.filter(rec => {
          const key = srcSids.map(sid => String(rec[sid]?.value ?? '')).join('|')
          return !existingKeys.has(key)
        })

        // Build options for the picker
        const displaySid = cfg.columns?.find(c => c.entityid)?.entityid
          ? qry.columns.find(c => c.entityid === Number(cfg.columns.find(cc => cc.entityid)?.entityid))?.entitysid
          : null
        const nameSid = displaySid || (groupEntity && this.entities?.[groupEntity]?.entity?.sid) || 'name'

        let selectedRecord = null

        if (isTextGroup) {
          // TEXT-group: always prompt for a new value (existing groups are already visible in UI)
          const { value: newValue } = await Swal.fire({
            title: 'Новая группа',
            input: 'text',
            inputLabel: (this.entities?.[groupEntity]?.entity?.name) || 'Значение',
            inputPlaceholder: 'Введите значение для новой группы',
            showCancelButton: true,
            confirmButtonText: 'Создать',
            cancelButtonText: 'Отмена',
            inputValidator: v => {
              if (!v || !v.trim()) return 'Введите значение'
              const exists = available.some(rec => String(rec[nameSid]?.value ?? '').trim().toLowerCase() === v.trim().toLowerCase())
                || existingSubs.some(sub => {
                  const key = String(sub.record?.[nameSid]?.value ?? '').trim().toLowerCase()
                  return key === v.trim().toLowerCase()
                })
              if (exists) return 'Такая группа уже есть'
              return null
            },
          })
          if (!newValue) return
          selectedRecord = {}
          if (qry.columns) qry.columns.forEach(col => { selectedRecord[col.entitysid] = { value: null, title: null } })
          if (nameSid) selectedRecord[nameSid] = { value: newValue.trim(), title: newValue.trim() }
        } else {
          if (available.length === 0) {
            await Swal.fire({
              title: 'Нет доступных значений',
              text: 'Все возможные значения уже добавлены в данную группу.',
              icon: 'info',
              confirmButtonText: 'OK',
            })
            return
          }

          const inputOptions = {}
          available.forEach((rec, idx) => {
            const display = rec[nameSid]?.title || rec[nameSid]?.value || rec.name?.value || `#${rec.id?.value || idx}`
            inputOptions[idx] = display
          })

          const result = await this.showSearchablePicker('Выберите значение', inputOptions)
          if (result === null) return

          selectedRecord = available[Number(result)]
          if (!selectedRecord) return
        }

        // Create the row from the query record (same as initSubRows)
        const rowColumns = this.getRowColumns(cfg, qry)
        const parentRec = isRoot ? null : parentRow
        const rec = this.getRowRecord(parentRec, selectedRecord, cfg)
        const cells = await this.getRowCells(rowColumns, selectedRecord, cfg, rec, parentRec)

        const newLevel = isRoot ? 1 : (parentRow.isFolder ? parentRow.level : parentRow.level + 1)
        const newLevelWithFolders = isRoot ? 1 : parentRow.level_with_folders + 1

        const newRow = {
          level: newLevel,
          level_with_folders: newLevelWithFolders,
          isExpanded: false,
          record: rec,
          cells,
          subs: [],
          status: 'ready',
          subStatus: 'empty',
          isFolder: selectedRecord.isfolder?.value,
          parent: selectedRecord.parent?.value,
          subParent: isRoot ? null : parentRow,
          id: selectedRecord.id?.value,
          addrecord: cfg.addrecord || false,
          mode: cfg.mode || 'list',
          cfg,
          zIndex: 1,
          parentRow: isRoot ? null : parentRow,
          subsQuery: null,
        }

        this.insertGroupRow(newRow, parentRow, isRoot)
      }
    },

    async showSearchablePicker(title, options) {
      // options can be plain { key: label } OR { key: { label, disabled, hint } }
      const entries = Object.entries(options)
      const escapeAttr = s => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
      const optionsHtml = entries.map(([key, val]) => {
        const isObj = val && typeof val === 'object'
        const label = isObj ? (val.label || '') : val
        const disabled = isObj ? !!val.disabled : false
        const hint = isObj ? (val.hint || '') : ''
        const cls = disabled ? 'swal-option swal-option--disabled' : 'swal-option'
        const style = disabled
          ? 'padding:6px 12px;cursor:not-allowed;color:#aaa;background:#fafafa;border-bottom:1px solid #eee;'
          : 'padding:6px 12px;cursor:pointer;border-bottom:1px solid #eee;'
        const hintHtml = hint ? `<span style="float:right;font-size:10px;color:#999;">${escapeAttr(hint)}</span>` : ''
        return `<div class="${cls}" data-key="${escapeAttr(key)}" data-label="${escapeAttr(String(label).toLowerCase())}" data-disabled="${disabled ? '1' : '0'}" style="${style}">${escapeAttr(label)}${hintHtml}</div>`
      }).join('')

      const result = await Swal.fire({
        title,
        html: `
          <input id="swal-search" type="text" class="swal2-input" placeholder="Поиск..." style="margin-bottom:8px;">
          <div id="swal-options" style="max-height:300px;overflow-y:auto;text-align:left;border:1px solid #ddd;border-radius:4px;">
            ${optionsHtml}
          </div>
          <input id="swal-selected" type="hidden" value="">
        `,
        showCancelButton: true,
        confirmButtonText: 'Добавить',
        cancelButtonText: 'Отмена',
        didOpen: () => {
          const search = document.getElementById('swal-search')
          const container = document.getElementById('swal-options')
          const items = container.querySelectorAll('.swal-option')
          const selectedInput = document.getElementById('swal-selected')

          search.focus()

          items.forEach(item => {
            if (item.dataset.disabled === '1') return // disabled items: not clickable
            item.addEventListener('click', () => {
              items.forEach(i => {
                if (i.dataset.disabled === '1') return
                i.style.background = ''
                i.style.color = ''
              })
              item.style.background = '#007bff'
              item.style.color = '#fff'
              selectedInput.value = item.dataset.key
            })
          })

          search.addEventListener('input', () => {
            const q = search.value.toLowerCase()
            items.forEach(item => {
              item.style.display = item.dataset.label.includes(q) ? '' : 'none'
            })
          })
        },
        preConfirm: () => {
          const val = document.getElementById('swal-selected').value
          if (!val) {
            Swal.showValidationMessage('Необходимо выбрать значение')
            return false
          }
          return val
        },
      })

      return result.isConfirmed ? result.value : null
    },

    insertGroupRow(newRow, parentRow, isRoot) {
      if (isRoot) {
        this.rows.push(newRow)
      } else {
        parentRow.subs = parentRow.subs || []
        parentRow.subs.push(newRow)
      }
      this.recalculateZIndices()

      this.$nextTick(() => {
        const newRx = this.getRows().indexOf(newRow)
        if (newRx >= 0) {
          this.selectedCell = { rx: newRx, ii: 0, i: -1 }
          this.updateShowPlusButton(newRow)
          this.updateShowDeleteButton(newRow)
          this.updateShowCopyButton(newRow)
          this.$nextTick(() => {
            this.ensureSelectedVisible()
            this.calculatePlusButtonPosition()
            this.calculateDeleteButtonPosition()
            this.calculateCopyButtonPosition()
          })
        }
      })
    },

    async addDeepRow(startRow) {
      let currentRow = startRow

      if (currentRow) {
        if (!currentRow.isExpanded) {
          this.onClickRowExpand(currentRow, 'row')
        }

        if (currentRow.subStatus === 'empty') {
          const cfg = this.getCfgForSubrows('row', currentRow)
          await this.initSubRowsSafe(currentRow, cfg)
        }
      }

      while (true) {
        const nextLevel = currentRow ? (currentRow.isFolder ? currentRow.level : currentRow.level + 1) : 1
        const cfg = this.formParam.rows[nextLevel - 1] || null
        if (!cfg) return

        const isLast = nextLevel === this.maxRowLevel()

        const newRow = this.createEditableRowForLevel(cfg, currentRow, nextLevel)

        this.insertRowToTree(newRow, currentRow)

        if (!isLast) {
          newRow.isExpanded = true
          const childCfg = this.getCfgForSubrows('row', newRow)
          await this.initSubRowsSafe(newRow, childCfg)
        }

        this.$nextTick(() => {
          const newRx = this.getRows().indexOf(newRow)
          if (newRx >= 0) {
            this.selectedCell = { rx: newRx, ii: 0, i: -1 }
            this.updateShowPlusButton(newRow)
            this.updateShowDeleteButton(newRow)
          }
        })

        if (isLast) {
          // BUG-782: пустую строку не сохраняем — сохранение без ввода упиралось в ограничения
          // («Статья БДР недоступна…») до того, как человек что-то ввёл. Строка сохранится после
          // ввода — уходом со строки или кнопкой «Сохранить». Исключение — форма с value_cell:
          // её ячейкам нужна уже записанная строка.
          if (this.formParam.value_cell) await this.saveRowRecord(newRow)
          break
        }

        currentRow = newRow
      }
    },
    async initSubRowsSafe(parentRow, cfg) {
      if (!cfg) {
        parentRow.subStatus = 'empty'
        parentRow.subs = []
        return
      }
      await this.initSubRows(parentRow, cfg)
      if (!parentRow.subs || parentRow.subs.length === 0) parentRow.subStatus = 'empty'
    },
    createEditableRowForLevel(cfg, parentRow, level) {
      const rec = this.getNewEditableRecord(cfg.objectid, parentRow)

      if (!(cfg.mode === 'record' || cfg.mode === 'tree_record')) {
        const pairs = cfg.results || (cfg.result ? [cfg.result] : [])
        pairs.forEach(p => {
          const destSid = this.entities?.[p.destination_entityid]?.entity?.sid
          if (destSid && rec[destSid]) {
            rec[destSid].value = rec[destSid].value ?? null
          }
        })
      }

      const cells = this.getCellsForEditableRecord(cfg, rec)

      return {
        level,
        level_with_folders: parentRow ? parentRow.level_with_folders + 1 : 1,
        isExpanded: false,
        record: rec,
        cells,
        subs: [],
        status: 'new',
        subStatus: 'empty',
        isFolder: false,
        parentRow: parentRow || null,
        cfg,
        addrecord: cfg.addrecord || false,
        mode: cfg.mode || 'list',
        zIndex: 1,
        lastChange: null,
        subsQuery: null,
      }
    },
    insertRowToTree(newRow, parentRow) {
      if (parentRow) {
        parentRow.subs = parentRow.subs || []
        parentRow.subs.unshift(newRow)
      } else {
        this.rows.unshift(newRow)
      }
      this.recalculateZIndices()
    },
    async newParentLine() {
      const cfg = this.formParam.rows[0]

      // For group-level root rows (non-record mode), use the picker
      if (cfg.mode !== 'record' && cfg.mode !== 'tree_record') {
        await this.addGroupRow(null, true)
        return
      }

      const newRow = this.getNewParentRecord(cfg)
      this.rows.push(newRow)
      this.recalculateZIndices()

      this.$nextTick(() => {
        const newRx = this.getRows().length - 1
        this.selectedCell = { rx: newRx, ii: 0, i: -1 }
        this.updateShowPlusButton(this.getRows()[newRx])
        this.updateShowDeleteButton(this.getRows()[newRx])
        this.$nextTick(() => {
          this.ensureSelectedVisible()
          this.calculatePlusButtonPosition()
          this.calculateDeleteButtonPosition()
        })
      })
    },
    newSiblingLine() {
      if (!this.showPlusButton) return
      if (!this.selectedCell) return
      const row = this.getRows()[this.selectedCell.rx]
      const newRow = this.getNewSubLine(row.parentRow)
      if (row.parentRow) {
        row.parentRow.subs.splice(row.parentRow.subs.indexOf(row) + 1, 0, newRow)
      } else {
        this.rows.splice(this.rows.indexOf(row) + 1, 0, newRow)
      }
    },
    newSubLine() {
      if (!this.showPlusButton) return
      if (!this.selectedCell) return
      const row = this.getRows()[this.selectedCell.rx]
      if (!row.isExpanded) this.onClickRowExpand(row, 'row')
      const newSubRow = this.getNewSubLine(row)
      row.subs.splice(0, 0, newSubRow)
    },
    getNewSubLine(parentRow) {
      const parentLevel = parentRow ? parentRow.level : 0
      const subLevel = parentRow ? parentRow.level + 1 : 1
      const subFolderLevel = parentRow ? parentRow.level_with_folders + 1 : 1
      const parentRecord = parentRow ? parentRow.records : this.record.record.record
      const parentCells = parentRow ? parentRow.cells.filter(c => c !== null).map(c => ({ title: null, column: c.column })) : []

      const cfg = this.formParam.rows[parentLevel]
      // console.log('cfg', cfg)
      const newRec = cfg.mode === 'record' ? this.getNewEditableRecord(cfg.objectid, parentRow) : JSON.parse(JSON.stringify(parentRecord))
      const cells = cfg.mode === 'record' ? this.getCellsForEditableRecord(cfg, newRec) : parentCells
      return {
        level: subLevel,
        level_with_folders: subFolderLevel,
        isExpanded: false,
        record: newRec,
        cells,
        subs: [],
        status: 'new',
        subStatus: 'empty',
        isFolder: false,
        parent: parentRow,
        id: null,
        addrecord: cfg.addrecord || false,
        mode: cfg.mode || null,
        cfg,
        parentRow,
      }
    },

    getNewParentRecord(cfg) {
      const newRec = cfg.mode === 'record' || cfg.mode === 'tree_record'
        ? this.getNewEditableRecord(cfg.objectid, null)
        : JSON.parse(JSON.stringify(this.record.record.record))

      let cells = []
      if (cfg.mode === 'record' || cfg.mode === 'tree_record') {
        cells = this.getCellsForEditableRecord(cfg, newRec)
      } else {
        cells = cfg.columns.map(col => ({
          title: null,
          value: null,
          column: { qryColumn: null, cfgColumn: col },
          mode: cfg.mode || 'list',
        }))
      }

      return {
        level: 1,
        level_with_folders: 1,
        isExpanded: false,
        record: newRec,
        cells,
        subs: [],
        status: 'new',
        subStatus: 'empty',
        isFolder: false,
        parent: null,
        id: null,
        addrecord: cfg.addrecord || false,
        mode: cfg.mode || 'list',
        cfg,
        parentRow: null,
        zIndex: 1,
        lastChange: null,
      }
    },

    async deleteLine() {
      if (!this.showDeleteButton || !this.selectedCell) return

      const row = this.getRows()[this.selectedCell.rx]
      if (!row) return

      const result = await Swal.fire({
        title: 'Удалить запись?',
        text: 'Вы уверены, что хотите удалить запись?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Да',
        cancelButtonText: 'Нет',
      })

      if (!result.isConfirmed) return

      await this.deleteRow(row)
    },
    async deleteRow(row) {
      if (!row) return

      // Удаление строки — мягкое (помечаем del = true), а не физическое удаление из БД.
      // Сохранённую строку убираем из таблицы только ПОСЛЕ успешного ответа сервера.
      // Раньше строка всегда удалялась из UI сразу, а серверный запрос был
      // «fire-and-forget» (без await) и к тому же физическим (removerecordunsafe):
      // при ошибке (FK/триггер) строка исчезала визуально, но оставалась в БД и
      // «возвращалась» после перезагрузки формы — отсюда баг «с первого раза не удаляется».
      if (row.id) {
        const res = await useCubus.delRecord(this, {
          objectid: row.cfg?.objectid,
          record: row.record,
          formid: this.objectFull.object.id,
        })
        if (!res) return // ошибка уже показана тостом — строку оставляем на месте
      }

      if (row.parentRow) {
        row.parentRow.subs.splice(row.parentRow.subs.indexOf(row), 1)
      } else {
        this.rows.splice(this.rows.indexOf(row), 1)
      }

      this.selectedCell = null
      this.showDeleteButton = false
      this.deleteButtonPosition = null
      this.recalculateZIndices()
    },

    currentlyEditing() {
      const ae = document.activeElement
      if (!ae) return false
      if (ae.contentEditable === 'true') return true
      if (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA') return true
      return !!ae.closest('.cbs-dropdown' || ae.closest('.cbs-dropdown-menu'))
    },
    // Разбирает вставляемый текст ref-ячейки -> { numVal: id, title }.
    // text может прийти как: сырой id ("2"), отображение "Title (id)" (внутренняя
    // копия), или просто ярлык "Title" (внешняя вставка). Раньше paste делал
    // Number(text); на "USD (2)" получал NaN и писал всю строку в значение, из-за
    // чего при рендере она снова оборачивалась в "(...)" и накручивалась поверх
    // при каждом копировании. Теперь id извлекается корректно, title берётся из
    // справочника (чистый), а нераспознанное не пишется мусором.
    resolveRefPaste(text, refData) {
      const raw = String(text == null ? '' : text).trim()
      let id = Number(raw)
      if (Number.isNaN(id)) {
        const m = raw.match(/\((\d+)\)\s*$/) // хвостовой (id) из "Title (id)"
        id = m ? Number(m[1]) : NaN
      }
      if (Number.isNaN(id)) {
        // вставка ярлыка без id — пробуем найти по заголовку
        const label = raw.replace(/\s*\(\d+\)\s*$/, '').trim()
        const byTitle = refData?.find(r => String(r.title).trim() === label)
        if (byTitle) return { numVal: byTitle.value, title: byTitle.title }
        // последний шанс — внутренний буфер копирования
        if (this.copiedCellValue && this.copiedCellValue.value != null) {
          return { numVal: this.copiedCellValue.value, title: this.copiedCellValue.title ?? null }
        }
        return { numVal: null, title: null }
      }
      const match = refData?.find(r => r.value === id)
      return { numVal: id, title: match ? match.title : (this.copiedCellValue?.title ?? null) }
    },
    onPaste(event) {
      if (!this._awaitingPaste || !this.selectedCell) {
        this._awaitingPaste = false
        return
      }
      this._awaitingPaste = false
      const text = (event.clipboardData || window.clipboardData).getData('text')
      if (!text) return
      event.preventDefault()
      const { rx, ii, i } = this.selectedCell
      const row = this.getRows()[rx]
      if (i >= 0) {
        const col = this.getColumns()[ii]
        const ve = this.valueEntities[i]
        if (this.isEditable(row, col, i, ii) && ve?.editable) {
          if (ve.entity.datatype?.sid === 'ref') {
            const { numVal, title } = this.resolveRefPaste(text, this.getRef(ve.entity.id))
            if (numVal != null) this.onUpdateRecord(row, col, ve, numVal, title)
          } else {
            const parsed = Number(text.replace(/\s/g, '').replace(',', '.'))
            const val = Number.isNaN(parsed) ? text.trim() : parsed
            this.onUpdateRecord(row, col, ve, val)
          }
        }
      } else {
        const rowCell = row.cells[ii]
        if (rowCell?.column?.cfgColumn?.editable && this.canEditRow(row)) {
          const entSid = rowCell.column.qryColumn.entitysid
          const isRef = rowCell.column.qryColumn.datatype === 'ref'
          if (isRef) {
            const { numVal, title } = this.resolveRefPaste(text, this.getRef(rowCell.column.qryColumn.entityid, row))
            if (numVal != null) this.onUpdateRowRecord(row, entSid, numVal, title)
          } else {
            const parsed = Number(text.replace(/\s/g, '').replace(',', '.'))
            const val = Number.isNaN(parsed) ? text.trim() : parsed
            this.onUpdateRowRecord(row, entSid, val)
          }
        }
      }
    },

    async onKeyDown(event) {
      // Ctrl+Z — отменить последнюю правку ячейки (одношагово). Если фокус в текстовом
      // поле — не перехватываем, пусть работает нативная отмена ввода.
      if ((event.metaKey || event.ctrlKey) && (event.key === 'z' || event.key === 'я' || event.key === 'Z')) {
        const ae = document.activeElement
        const inEditable = ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable)
        if (!inEditable && this.lastEdit) {
          event.preventDefault()
          this.undoLastEdit()
        }
        return
      }
      if (!this.selectedCell) {
        return
      }
      if (event.metaKey || event.ctrlKey) {
        if (event.key === 'c' || event.key === 'с') {
          // Ctrl+C — copy current cell value
          const { rx, ii, i } = this.selectedCell
          const row = this.getRows()[rx]
          if (i >= 0) {
            const col = this.getColumns()[ii]
            const ve = this.valueEntities[i]
            const rec = this.getCellRecord(row, col, false)
            if (rec && ve) {
              const val = rec[ve.entity.sid]?.value
              const title = rec[ve.entity.sid]?.title
              this.copiedCellValue = {
                value: val, title, type: 'value', veSid: ve.entity.sid, datatype: ve.entity.datatype?.sid,
              }
              if (val != null) {
                navigator.clipboard.writeText(String(val)).catch(() => {})
              }
            }
          } else {
            const rowCell = row.cells[ii]
            if (rowCell) {
              const displayVal = this.formatRowCell(row, rowCell)
              const entSid = rowCell.column?.qryColumn?.entitysid
              const rawVal = row.record?.[entSid]?.value ?? displayVal
              // храним ЧИСТЫЙ title (без "(id)"), а не отображение — иначе при
              // вставке ref он накручивался поверх ("USD (2)" -> "USD (2) (USD (2))").
              const rawTitle = row.record?.[entSid]?.title ?? null
              this.copiedCellValue = {
                value: rawVal,
                title: rawTitle,
                type: 'row',
                entitysid: entSid,
                datatype: rowCell.column?.qryColumn?.datatype,
              }
              navigator.clipboard.writeText(String(displayVal)).catch(() => {})
            }
          }
        } else if (event.key === 'v' || event.key === 'м') {
          // Ctrl+V — let the paste event handle it via onPaste()
          this._awaitingPaste = true
        }
        return
      }
      // eslint-disable-next-line no-nested-ternary
      let eventKey = event.key
      // eslint-disable-next-line no-nested-ternary
      const startChar = /^[0-9]$/.test(eventKey) ? eventKey
        : ((eventKey === 'Backspace' || eventKey === 'Delete') ? '' : null)

      let curEditing = this.currentlyEditing()
      // console.log('curEditing', curEditing)

      if (eventKey === 'Enter' || eventKey === 'F2' || startChar !== null) {
        if (curEditing) {
          if (eventKey === 'Enter') {
            eventKey = 'ArrowRight'
            // console.log('going forward')
            this.commitActiveEditor()
            curEditing = false // we emulate it to let commit
          } else {
            return
          }
        } else {
          event.preventDefault()
          const { rx, i, ii } = this.selectedCell
          const row = this.getRows()[rx]
          const col = this.getColumns()[ii]
          if (this.isEditable(row, col, i, ii)) {
            // console.log('1-b')
            this.editSelectedCell(startChar)
          } else if (eventKey === 'Enter') {
            if (this.onClickRowExpand(row, 'row')) return
            eventKey = 'ArrowRight'
          }
        }
      }

      if (curEditing) return

      event.preventDefault()

      if (eventKey === 'n' || eventKey === 'N' || eventKey === 'т' || eventKey === 'Т') {
        this.newLine()
        return
      }

      const { rx, ii, i } = this.selectedCell
      let newRx = rx
      let newIi = ii
      let newI = i
      const maxRcx = this.getRows()[rx].cells.length - 1

      switch (eventKey) {
        case 'ArrowUp':
          newRx = Math.max(0, rx - 1)
          break
        case 'ArrowDown':
          newRx = Math.min(this.getRows().length - 1, rx + 1)
          break
        case 'ArrowLeft': {
          const row = this.getRows()[rx]
          const maxHeaderIdx = this.getRows()[rx].cells.length - 1
          const isHeader = (i === -1)
          // const atLeftmostHeader = isHeader && (ii === 0)
          if (isHeader) {
            if (ii > 0) {
              newIi = ii - 1
              newI = -1
              break
            }
            const canExpandOrCollapse = this.isExpandable(row)
            if (canExpandOrCollapse && row.isExpanded) {
              this.onClickRowExpand(row, 'row')
              newRx = rx; newIi = 0; newI = -1
            } else {
              const jump = this.goToParent(row)
              if (jump) { newRx = jump.newRx; newIi = jump.newIi; newI = jump.newI } else { newRx = rx; newIi = 0; newI = -1 }
            }
            break
          }

          if (i > 0) {
            newI = i - 1
          } else if (ii > 0 && i >= 0) {
            newIi = ii - 1
            newI = this.valueEntities.length - 1
          } else if (i === 0) {
            newI = -1
            newIi = maxHeaderIdx
          } else if (ii > 0) {
            newIi = ii - 1
            newI = -1
          } else {
            const canExpandOrCollapse = this.isExpandable(row)
            if (canExpandOrCollapse && row.isExpanded) {
              this.onClickRowExpand(row, 'row')
              newRx = rx; newIi = 0; newI = -1
            } else {
              const jump = this.goToParent(row)
              if (jump) { newRx = jump.newRx; newIi = jump.newIi; newI = jump.newI } else { newRx = rx; newIi = 0; newI = -1 }
            }
          }
          break
        }

        case 'ArrowRight':
          if (i < 0) {
            const row = this.getRows()[rx]
            const expandable = row.level < this.maxRowLevel() || row.isFolder
            if (expandable && !row.isExpanded && false) {
              this.onClickRowExpand(row, 'row')
            } else if (ii < maxRcx) {
              newIi = ii + 1
            } else {
              newIi = 0
              newI = 0
            }
          } else if (i < this.valueEntities.length - 1) {
            newI = i + 1
          } else if (ii < this.getColumns().length - 1) {
            newIi = ii + 1
            newI = 0
          }
          break
        default:
          break
      }
      // console.log('onKeyDown', newRx, newIi, newI)
      if ((eventKey === 'ArrowUp' || eventKey === 'ArrowDown') && newRx !== rx) {
        if (!(this._dirtyRows instanceof Set)) this._dirtyRows = new Set()

        const rows = this.getRows?.() || []
        const curRow = rows[rx]
        if (curRow && curRow.mode === 'record') {
          const key = curRow?.id || curRow?.record?.id || rx
          if (key != null && this._dirtyRows.has(key)) {
            let saved = false
            try {
              saved = await this.saveRowRecord(curRow)
            } catch (err) {
              console.error('Ошибка сохранения строки:', err)
            }
            if (saved) this.clearRowDirty(curRow, rx)
          }
        }
      }

      this.selectedCell = { rx: newRx, ii: newIi, i: newI }
      this.updateShowPlusButton(this.getRows()[newRx])
      this.updateShowDeleteButton(this.getRows()[newRx])
      this.updateShowCopyButton(this.getRows()[newRx])
      this.$nextTick(() => {
        this.ensureSelectedVisible()
        this.calculatePlusButtonPosition()
        this.calculateDeleteButtonPosition()
        this.calculateCopyButtonPosition()
      })
    },
    updateShowCopyButton(row) {
      const parent = this.getRowParent(row)
      const canAddHere = !!row.addrecord || (parent && !!parent.addrecord)
      this.showCopyButton = canAddHere && (row.mode === 'record' || row.mode === 'tree_record') && row.status !== 'loading'
        && this.canDo('insert', row.cfg?.objectid)
    },

    calculateCopyButtonPosition() {
      if (!this.selectedCell || !this.showCopyButton || this.currentlyEditing()) {
        this.copyButtonPosition = null
        return
      }

      const { rx, ii, i } = this.selectedCell
      const refName = `td_${rx}_${ii}_${i}`
      const ref = this.$refs[refName]
      const cell = Array.isArray(ref) ? ref[0] : ref
      if (!cell) { this.copyButtonPosition = null; return }

      const cellRect = cell.getBoundingClientRect()
      const scrollContainer = this.$el.querySelector('.pivot-wrapper')
      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect()
        const isVisible = cellRect.bottom > containerRect.top
            && cellRect.top < containerRect.bottom
            && cellRect.right > containerRect.left
            && cellRect.left < containerRect.right
        if (!isVisible) { this.copyButtonPosition = null; return }
      }

      const baseLeft = cellRect.left + cellRect.width / 2 - 10
      this.copyButtonPosition = {
        top: cellRect.bottom + 5,
        left: baseLeft + 28,
      }
    },

    editSelectedCell(seed) {
      if (!this.selectedCell) return
      const { rx, ii, i } = this.selectedCell
      const rows = this.getRows()
      const columns = this.getColumns()
      if (rx >= rows.length || i >= this.valueEntities.length) return
      const row = rows[rx]
      const col = columns[ii]
      const ve = this.valueEntities[i]
      this.$nextTick(() => this.ensureSelectedVisible())
      this.onCellClick(rx, ii, i, ve, row, col, seed, true)
    },

    indentStyle(row, cell) {
      // console.log(`cell = ${JSON.stringify(cell)}`)
      if (cell && cell.column && Object.hasOwn(cell.column.cfgColumn, 'indent') && cell.column.cfgColumn.indent === true) {
        return { 'margin-left': `${row.level_with_folders - 1}rem` }
      }
      return {}
    },
    rowSpanStyle() {
      let rowSpan = 1
      if (this.formParam.value_cell && this.formParam.value_cell.results.length > 1) rowSpan += 1
      // if (this.formParam.grand_total) rowSpan += 1
      return rowSpan
    },

    debugFilter(row, col) {
      const f = this.totalCellFilter(row, col)
      console.log(toWhereString(f))
      // console.log('paramRecord ', this.paramRecord(row, col))
      // console.log(`row = ${JSON.stringify(row)}`)
      // console.log(`col = ${JSON.stringify(col)}`)
    },

    calculatePlusButtonPosition() {
      if (!this.selectedCell || !this.showPlusButton || this.currentlyEditing()) {
        this.plusButtonPosition = null
        return
      }

      const { rx, ii, i } = this.selectedCell
      const refName = `td_${rx}_${ii}_${i}`
      const ref = this.$refs[refName]
      const cell = Array.isArray(ref) ? ref[0] : ref

      if (!cell) {
        this.plusButtonPosition = null
        return
      }

      const cellRect = cell.getBoundingClientRect()
      const scrollContainer = this.$el.querySelector('.pivot-wrapper')

      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect()

        const isVisible = cellRect.bottom > containerRect.top
            && cellRect.top < containerRect.bottom
            && cellRect.right > containerRect.left
            && cellRect.left < containerRect.right

        if (!isVisible) {
          this.plusButtonPosition = null
          return
        }
      }

      this.plusButtonPosition = {
        top: cellRect.bottom + 5,
        left: cellRect.left + cellRect.width / 2 - 10,
      }
    },

    calculateDeleteButtonPosition() {
      if (!this.selectedCell || !this.showDeleteButton) {
        this.deleteButtonPosition = null
        return
      }

      const { rx } = this.selectedCell
      const scroller = this.$el.querySelector('.pivot-wrapper')
      const tbody = this.$el.querySelector('.pivot-table tbody')
      if (!scroller || !tbody) { this.deleteButtonPosition = null; return }

      const tr = tbody.querySelectorAll('tr')[rx]
      if (!tr) { this.deleteButtonPosition = null; return }

      const trRect = tr.getBoundingClientRect()
      const wrapRect = scroller.getBoundingClientRect()

      const visible = trRect.bottom > wrapRect.top
          && trRect.top < wrapRect.bottom
          && trRect.right > wrapRect.left
          && trRect.left < wrapRect.right
      if (!visible) { this.deleteButtonPosition = null; return }

      this.deleteButtonPosition = {
        top: trRect.top + trRect.height / 2 - 10,
        left: wrapRect.left - 25,
      }
    },

    getClassCellSelected(rx, rcx, row, cell) {
      const isEditable = (row.mode === 'record' || row.mode === 'tree_record') && cell && cell.column.cfgColumn.editable === true
      const isRef = isEditable && cell.column.qryColumn.datatype === 'ref'
      // Required if marked in form_param OR if the row's source object has a
      // notnull-style constraint on this cell's entity (matched by sid or by `#<entityid>`).
      const constraintSet = this.constraintRequiredSids[row?.cfg?.objectid]
      const cellSid = cell?.column?.qryColumn?.entitysid
      const cellEid = cell?.column?.qryColumn?.entityid
      const isConstraintRequired = !!constraintSet && (
        (cellSid && constraintSet.has(cellSid))
        || (cellEid && constraintSet.has(`#${cellEid}`))
      )
      const isRequiredEmpty = isEditable
        && (cell.column.cfgColumn.required === true || isConstraintRequired)
        && this.isCellEmpty(row, cell)
      return {
        'cell-selected': this.selectedCell && this.selectedCell.rx === rx && this.selectedCell.ii === rcx && this.selectedCell.i === -1,
        'cell-yellow': isEditable && !isRef && !isRequiredEmpty,
        'cell-ref': isRef && !isRequiredEmpty,
        'cell-required-empty': isRequiredEmpty,
      }
    },
    isCellEmpty(row, cell) {
      if (!cell || !cell.column?.qryColumn?.entitysid) return false
      const entSid = cell.column.qryColumn.entitysid
      const v = row.record?.[entSid]?.value
      if (v === null || v === undefined) return true
      if (typeof v === 'string' && v.trim() === '') return true
      // numeric ref ids: 0 is treated as empty (no record selected)
      if (cell.column.qryColumn.datatype === 'ref' && (v === 0 || v === '0')) return true
      return false
    },
    async loadConstraintsForRows() {
      // Fetch constraints for each source object via dedicated backend endpoint —
      // doesn't require the user to have read access on the `object_constraint` catalog.
      try {
        const objectIds = new Set();
        (this.formParam?.rows || []).forEach(row => {
          if (row?.objectid) objectIds.add(Number(row.objectid))
        })
        if (objectIds.size === 0) return
        const useJwt = require('@/cubus/jwt/useJwt').default
        const token = useJwt.getCubusToken()
        const map = {}
        await Promise.all([...objectIds].map(async objId => {
          try {
            const resp = await useJwt.query({
              token,
              query: { method: 'objectconstraints', mode: 'sync', param: { objectid: objId } },
            })
            const list = resp?.data?.result?.constraints || []
            list.forEach(c => {
              const sids = this.extractNotnullSidsFromFilter(c.jsonfilter)
              if (sids.length === 0) return
              if (!map[objId]) map[objId] = new Set()
              sids.forEach(sid => map[objId].add(sid))
            })
          } catch (e) { /* skip this object — usually means backend not yet up */ }
        }))
        this.constraintRequiredSids = map
      } catch (e) {
        console.warn('loadConstraintsForRows failed', e)
      }
    },
    extractNotnullSidsFromFilter(filterJson) {
      // Supports both filter formats: rich (type.sid + value.entitysid) and compact (argtype + argvalue with entityid).
      const out = []
      const walk = node => {
        if (!node || typeof node !== 'object') return;
        (node.conds || []).forEach(c => {
          const op = c?.oper?.sid
          if (op !== 'notnull' && op !== 'isnotnull' && op !== 'greater') return
          const left = c.args?.[0]
          if (!left) return
          // Rich format
          if (left.type?.sid === 'entity') {
            const sid = left.value?.entitysid
            if (sid) { out.push(sid); return }
            const eid = left.value?.value || left.entityid
            if (eid) out.push(`#${eid}`)
            return
          }
          // Compact format (Filter.toJson on backend)
          if (left.argtype === 'entity') {
            const eid = left.argvalue
            if (eid) out.push(`#${eid}`)
          }
        });
        (node.nodes || []).forEach(walk)
      }
      walk(filterJson?.node || filterJson)
      return out
    },
    getMissingRequiredFields(row) {
      // Returns array of column display names that are marked required in form_param but empty in this row.
      const cfg = row?.cfg
      if (!cfg || !Array.isArray(cfg.columns)) return []
      const missing = []
      cfg.columns.forEach((col, idx) => {
        if (col?.required !== true) return
        const ent = this.entities?.[col.entityid]?.entity
        const sid = ent?.sid
        if (!sid) return
        const v = row.record?.[sid]?.value
        const isEmpty = v === null || v === undefined
          || (typeof v === 'string' && v.trim() === '')
          || (ent?.datatype?.sid === 'ref' && (v === 0 || v === '0'))
        if (isEmpty) {
          const label = (this.formParam?.column_heads || [])[idx] || ent?.name || sid
          missing.push(label)
        }
      })
      return missing
    },
    escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, c => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      }[c]))
    },

    async initObjects() {
      for (let i = 0; i < this.formParam.rows.length; i += 1) {
        const cfg = this.formParam.rows[i]
        if (cfg.mode && (cfg.mode === 'record' || cfg.mode === 'tree_record')) {
          // eslint-disable-next-line no-await-in-loop
          const obj = await useCubus.loadObject(this, { objectid: cfg.objectid })
          this.$set(this.objects, cfg.objectid, obj)
        }
      }
    },

    getNewEditableRecord(objectId, parentRow) {
      const obj = this.objects[objectId]
      if (!obj) return null
      const n = {}
      for (let i = 0; i < obj.attributes.length; i += 1) {
        const attr = obj.attributes[i]
        n[attr.entitysid] = { value: attr.default.value, title: attr.default.title }
      }
      // Fill auto-computed fields
      if (n.createdat && !n.createdat.value) {
        n.createdat = { value: new Date().toISOString(), title: null }
      }
      if (n.authorsid && !n.authorsid.value) {
        const userSid = this.$store?.getters['cubus-store/SESSION']?.user?.sid
        if (userSid) n.authorsid = { value: userSid, title: null }
      }
      const parentRecord = parentRow ? parentRow.record : this.record.record.record
      Object.keys(parentRecord).forEach(key => {
        // Don't overwrite computed defaults (createdat, author) with null parent values
        if (n[key]?.value != null && (parentRecord[key]?.value == null)) return
        n[key] = { value: parentRecord[key].value, title: parentRecord[key].title }
      })
      return n
    },
    getCellsForEditableRecord(cfg, record) {
      // console.log('cfg', cfg)
      const obj = this.objects[cfg.objectid]
      if (!obj) return []
      const cells = []
      for (let i = 0; i < cfg.columns.length; i += 1) {
        const col = cfg.columns[i]
        // console.log('col', col)
        if (col && col.entityid) {
          const attr = obj.attributes.find(a => a.entityid === col.entityid)
          console.log('attr', attr)
          const cell = {
            title: record[attr.entitysid].title || record[attr.entitysid].value,
            column: { qryColumn: attr, cfgColumn: col },
          }
          cells.push(cell)
        } else {
          cells.push({
            title: null,
            column: { cfgColumn: { editable: false } },
          })
        }
      }
      return cells
    },

    async initRefs_v1(qry) {
      // console.log('initRefs', qry.columns)
      const cols = qry.columns.filter(c => c.datatype === 'ref')
      const params = []
      const stack = {}
      for (let i = 0; i < cols.length; i += 1) {
        // const name = useCubus.guid()
        const col = cols[i]
        const name = col.entitysid
        if (!Object.hasOwn(this.qryRefs, col.entitysid)) {
          this.$set(this.qryRefs, col.entitysid, null)
          params.push({
            name,
            method: 'query',
            mode: 'sync',
            param: {
              keymode: 'sid',
              objectid: col.refobjectid,
              filter: Q.and([Q.equals('del', false)]),
              limit: 50000,
              orderby: Q.orderBy(['name']),
              entityids: [1, 2, 33, 3, 32],
            },
          })
          stack[name] = col.entitysid
        }
        if (!Object.hasOwn(this.refs, col.entitysid)) {
          this.$set(this.refs, col.entitysid, null)
          params.push({
            name: `reference_${name}`,
            method: 'reference',
            mode: 'sync',
            param: {
              entity_sid: col.entitysid,
              destination_object_id: this.objectFull.object.id,
            },
          })
          stack[`reference_${name}`] = col.entitysid
        }
      }
      if (params.length > 0) {
        // console.log('stack', stack)
        const multi = await useCubus.loadMultiQuery(this, { requests: params })
        this.multi = multi
        // console.log('multi', multi)
        const { results } = multi.result
        for (let i = 0; i < results.length; i += 1) {
          const result = results[i]
          const entitysid = stack[result.name]
          if (result.name.startsWith('reference_')) {
            this.refs[entitysid] = result.result.reference
            this.$set(this.refRowDep, entitysid, !!result.result.rowdependent)
          } else {
            this.qryRefs[entitysid] = result.result.query
          }
        }
        // console.log('qryRefs', this.qryRefs)
      }
    },
    async initRefs(qry) {
      const cols = qry.columns.filter(c => c.datatype === 'ref')
      const params = []
      const stack = {}
      for (let i = 0; i < cols.length; i += 1) {
        const col = cols[i]
        const name = col.entitysid
        if (!Object.hasOwn(this.refs, col.entitysid)) {
          this.$set(this.refs, col.entitysid, null)
          params.push({
            name,
            method: 'reference',
            mode: 'sync',
            param: {
              entity_sid: col.entitysid,
              destination_object_id: this.objectFull.object.id,
            },
          })
          stack[name] = col.entitysid
        }
      }
      if (params.length > 0) {
        const multi = await useCubus.loadMultiQuery(this, { requests: params })
        this.multi = multi
        const { results } = multi.result
        for (let i = 0; i < results.length; i += 1) {
          const result = results[i]
          const entitysid = stack[result.name]
          this.refs[entitysid] = result.result.reference
          this.$set(this.refRowDep, entitysid, !!result.result.rowdependent)
        }
        // console.log('qryRefs', this.qryRefs)
      }
    },
    async initRef(col) {
      if (Object.hasOwn(this.qryRefs, col.entitysid)) return
      this.$set(this.qryRefs, col.entitysid, null)
      const qry = await this.loadQuery(this, {
        keymode: 'sid',
        objectid: col.refobjectid,
        filter: Q.and([Q.equals('del', false)]),
        limit: 50000,
        orderby: Q.orderBy(['name']),
        entityids: [1, 2, 33, 3, 11],
      })
      this.qryRefs[col.entitysid] = qry
    },
    getRef(entityid, row = null) {
      const ent = this.entities[entityid]
      if (!ent) return []
      if (!this.refs[ent.entity.sid]) return []
      const entitysid = ent.entity.sid
      const list = (row && this.refRowDep[entitysid]) ? this.getRowRef(entitysid, row) : this.refs[entitysid]
      if (!list) return []
      const res = list.map(r => ({
        value: r.value,
        title: r.title,
        sid: r.sid,
        isfolder: !!r.isfolder,
        // disabled lets CbsReferencePicker3 render folders as greyed-out (already-existing template branch)
        disabled: !!r.isfolder,
      }))
        .sort((a, b) => a.title.localeCompare(b.title))
      return res
    },

    // PROP-783: правило доступности ссылки зависит от строки (paramentity) — список грузится по
    // значениям строки (paramrecord) и кэшируется по ним; пока грузится — пусто, а не список формы,
    // чтобы не предложить недоступное значение.
    rowParamRecord(row) {
      const obj = this.objects[row?.cfg?.objectid]
      const sids = obj ? new Set(obj.attributes.map(a => a.entitysid)) : null
      const rec = {}
      Object.keys(row?.record || {}).forEach(k => {
        const c = row.record[k]
        if (c && typeof c === 'object' && 'value' in c && (!sids || sids.has(k))) rec[k] = { value: c.value }
      })
      return rec
    },
    getRowRef(entitysid, row) {
      const rec = this.rowParamRecord(row)
      const key = `${entitysid}|${JSON.stringify(rec)}`
      if (Object.hasOwn(this.rowRefs, key)) return this.rowRefs[key]
      this.$set(this.rowRefs, key, null)
      useCubus.loadMultiQuery(this, {
        requests: [{
          name: 'ref',
          method: 'reference',
          mode: 'sync',
          param: {
            entity_sid: entitysid,
            destination_object_id: this.objectFull.object.id,
            paramrecord: { keymode: 'sid', record: rec },
          },
        }],
      }).then(multi => {
        const ref = multi?.result?.results?.[0]?.result?.reference
        this.$set(this.rowRefs, key, Array.isArray(ref) ? ref : [])
      }).catch(e => {
        console.error(e)
        this.$set(this.rowRefs, key, [])
      })
      return null
    },

    getRowRecordCellStyle(cell) {
      if (cell && cell.column.cfgColumn.align) {
        return { textAlign: cell.column.cfgColumn.align }
      }
      return {}
    },
    isExpandable(row) {
      return (row.level < this.maxRowLevel()) || !!row.isFolder
    },
    goToParent(row) {
      const parentRow = row.subParent
      if (!parentRow) return null
      return {
        newRx: this.getRows().indexOf(parentRow),
        newIi: 0,
        newI: -1,
      }
    },
    toNumber(raw) {
      if (raw == null || raw === '') return null
      const s = typeof raw === 'string' ? raw.replace(/\s/g, '').replace(',', '.') : raw
      const n = Number(s)
      return Number.isFinite(n) ? n : null
    },

    formatByDatatype(cell, datatype, opts = {}) {
      const rawVal = (cell && typeof cell === 'object' && 'value' in cell) ? cell.value : cell
      const rawTitle = (cell && typeof cell === 'object' && 'title' in cell) ? cell.title : undefined

      const has = v => !(v === null || v === undefined || v === '')
      const display = has(rawTitle) ? rawTitle : rawVal
      const calcSrc = has(rawVal) ? rawVal : rawTitle

      if (!has(display) && !has(calcSrc)) return ''
      switch ((datatype || '').toLowerCase()) {
        case 'double': {
          const num = this.toNumber(calcSrc)
          if (num == null) return String(display)
          if (num === 0) return '-'
          const digits = opts.decimals ?? 2
          return num.toLocaleString('ru-RU', {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
          })
        }
        case 'decimal':
        case 'number':
        case 'int':
        case 'integer': {
          const num = this.toNumber(calcSrc)
          if (num == null) return String(display)
          if (num === 0) return '-'
          const digits = opts.decimals ?? 0
          return num.toLocaleString('ru-RU', {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
          })
        }
        // BUG-784: numeric (суммы бюджета) падал в default и показывался сырой строкой
        // «420000.00» / «0.00». Разряды, копейки — только если есть (или decimals колонки), ноль — «-».
        case 'numeric': {
          const num = this.toNumber(calcSrc)
          if (num == null) return String(display)
          if (num === 0) return '-'
          return num.toLocaleString('ru-RU', {
            minimumFractionDigits: opts.decimals ?? 0,
            maximumFractionDigits: opts.decimals ?? 2,
          })
        }
        case 'date': {
          if (!has(calcSrc) || calcSrc === '0') return ''
          const d = new Date(calcSrc)
          // единый формат ДД.ММ.ГГГГ (как в редакторе-пикере)
          return Number.isNaN(d.getTime()) ? String(display) : d.toLocaleDateString('ru-RU')
        }
        case 'datetime':
        case 'timestamp': {
          if (!has(calcSrc) || calcSrc === '0') return ''
          const d = new Date(calcSrc)
          // в форме ввода время не показываем — только дата ДД.ММ.ГГГГ
          return Number.isNaN(d.getTime()) ? String(display) : d.toLocaleDateString('ru-RU')
        }
        case 'time': {
          if (!has(calcSrc) || calcSrc === '0') return ''
          const d = new Date(`1970-01-01T${String(calcSrc).slice(0, 8)}`)
          return Number.isNaN(d.getTime()) ? String(display) : d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
        }
        case 'bool':
        case 'boolean': {
          const v = String(calcSrc).toLowerCase()
          const b = (v === 'true' || v === '1' || v === 'да' || v === 'yes')
          return b ? 'Да' : 'Нет'
        }
        case 'ref':
          if (!has(calcSrc) || calcSrc === 0) return ''
          return has(rawTitle) ? `${String(rawTitle)} (${calcSrc})` : `(${calcSrc})`
        case 'text':
        default:
          return String(display)
      }
    },
    formatRowCell(row, rowCell) {
      if (!rowCell) return ''
      const type = this.getCellDatatype(rowCell)
      const decimals = rowCell?.column?.cfgColumn?.decimals
      let payload
      if ((row.mode === 'record' || row.mode === 'tree_record') && rowCell?.column?.qryColumn?.entitysid) {
        const entSid = rowCell.column.qryColumn.entitysid
        payload = row.record?.[entSid]
      } else {
        payload = (rowCell.value !== undefined) ? { value: rowCell.value, title: rowCell.title } : rowCell.title
      }
      // Мемоизация: форматирование зависит только от value/title/type/decimals,
      // но toLocaleString/new Date дорогие, а formatRowCell зовётся на каждую ячейку
      // при каждом рендере (скролл/выбор/разворот). Кэшируем результат на ячейке и
      // пересчитываем только при изменении значения. Чтение value/title оставляет
      // реактивную зависимость, поэтому при правке ячейки ключ меняется и кэш
      // инвалидируется автоматически. _fmt/_fmtKey — обычные (нереактивные) поля.
      const pv = (payload && typeof payload === 'object') ? payload.value : payload
      const pt = (payload && typeof payload === 'object') ? payload.title : undefined
      const key = `${type}|${decimals}|${pv}|${pt}`
      if (rowCell._fmtKey === key) return rowCell._fmt
      const res = this.formatByDatatype(payload, type, { decimals })
      rowCell._fmt = res
      rowCell._fmtKey = key
      return res
    },
    getCellDatatype(cell) {
      const qryDt = cell?.column?.qryColumn?.datatype
      if (qryDt) return qryDt
      const destEntId = cell?.column?.cfgColumn?.destination_entityid
      if (destEntId) {
        return this.entities[destEntId]?.entity.datatype.sid
      }
      return null
    },

    // recalcGrandTotal() {
    //   for (let i = 0; i < this.formParam.grand_total.column_heads.length; i += 1) {
    //     const col = this.formParam.grand_total.column_heads[i]
    //   }
    // },
    getGtValue(index) {
      if (!Number.isInteger(index) || index < 0) return null
      const colHeads = this?.formParam?.grand_total?.column_heads
      if (!Array.isArray(colHeads) || index >= colHeads.length) return null

      const colCfg = colHeads[index]
      if (!colCfg || colCfg.type !== 'sumrows') return null

      const rows = Array.isArray(this?.rows) ? this.rows : []
      const val = rows.reduce((sum, row) => {
        const raw = row?.cells?.[index]?.value
        // eslint-disable-next-line no-nested-ternary
        const num = typeof raw === 'number' ? raw : typeof raw === 'string' ? parseFloat(raw.replace(/\s|\u00A0/g, '').replace(',', '.')) : 0
        return sum + (Number.isFinite(num) ? num : 0)
      }, 0)
      if (val === 0) return '-'

      const entity = this?.entities?.[colCfg.entityid]?.entity
      if (!entity?.datatype?.sid) return val

      const sid = String(entity.datatype.sid).toLowerCase()
      const format = (min, max) => new Intl.NumberFormat('ru-RU', { minimumFractionDigits: min, maximumFractionDigits: max }).format(val)

      if (sid === 'double') return format(2, 2)
      if (sid === 'int') return format(0, 0)
      if (sid === 'numeric') return format(0, 2)

      return val
    },
    async loadScalarValue(scalarId, paramRecord) {
      try {
        const cell = await useCubus.loadScalarValue(this, {
          scalarid: scalarId,
          record: paramRecord,
        })
        // console.log('cell', cell)
        return cell
      } catch (e) {
        console.error(e)
        return null
      }
    },
    isAddRecord() {
      return this.formParam.rows.some(r => r.addrecord && r.addrecord === true)
    },
    isEmptyTable() {
      return this.rows.length === 0
    },
    isNewParentRowVisible() {
      const rootCfg = this.formParam?.rows?.[0]
      if (rootCfg && rootCfg.addrecord === false) return false
      if (!this.canDo('insert', rootCfg?.objectid)) return false
      if (this.isAddRecord() && this.isEmptyTable()) return true
      if (rootCfg && rootCfg.addrecord && rootCfg.mode !== 'record' && rootCfg.mode !== 'tree_record') return true
      return false
    },

    checkLastChange(row, col, currentChange) {
      this.checkLastChangeRow(row, currentChange)
      this.checkLastChangeRow(col, currentChange)
    },
    checkLastChangeRow(row, currentChange) {
      // console.log(row.lastChange, currentChange)
      if (!currentChange) return
      const r = row
      if (!row.lastChange) {
        r.status = 'ready'
        return
      }
      if (row.lastChange === currentChange) {
        r.lastChange = null
        r.status = 'ready'
      }
    },

    markRowDirty(row) {
      const set = this.ensureDirtySet()
      const key = this.rowKey(row)
      if (key != null) set.add(key)
      this.dirtyTick += 1
    },

    // _dirtyRows не реактивен (Set), dirtyTick — зависимость для признака «не сохранено».
    isRowDirty(row) {
      if (this.dirtyTick < 0) return false
      const set = this.ensureDirtySet()
      const key = this.rowKey(row)
      return key != null && set.has(key)
    },
    clearRowDirty(row, fallback) {
      const key = this.rowKey(row, fallback)
      if (key != null) this.ensureDirtySet().delete(key)
      this.dirtyTick += 1
    },
    dirtyCount() {
      if (this.dirtyTick < 0) return 0
      return (this.getRows?.() || []).filter(r => r.mode === 'record' && this.isRowDirty(r)).length
    },
    async saveDirtyRows() {
      if (this.savingDirty) return
      this.savingDirty = true
      try {
        const active = document.activeElement
        if (active && active.getAttribute('contenteditable') === 'true') active.blur()
        await this.$nextTick()
        const dirty = (this.getRows?.() || []).filter(r => r.mode === 'record' && this.isRowDirty(r))
        for (let k = 0; k < dirty.length; k += 1) {
          // eslint-disable-next-line no-await-in-loop
          if (await this.saveRowRecord(dirty[k])) this.clearRowDirty(dirty[k])
          else break
        }
      } finally {
        this.savingDirty = false
      }
    },
    onBeforeUnload(e) {
      if (this.dirtyCount() === 0) return undefined
      e.preventDefault()
      e.returnValue = ''
      return ''
    },

    async commitRowIfNeeded(prevRx) {
      if (prevRx == null) return
      const rows = this.getRows?.() || []
      const prevRow = rows[prevRx]
      if (!prevRow || prevRow.mode !== 'record') return
      if (!this.isRowDirty(prevRow)) return

      // BUG-782: несохранённая (отказ, не заполнено) строка остаётся помеченной — признак не гаснет молча.
      if (await this.saveRowRecord(prevRow)) this.clearRowDirty(prevRow, prevRx)
    },

    _onGlobalMouseDown(e) {
      if (!this.$el) return

      // 1) клики по плавающим кнопкам – не трогаем строку
      const floatingBtn = e.target.closest(
        '.floating-plus-button, .floating-delete-button, .floating-copy-button',
      )
      if (floatingBtn) return

      // 2) клики внутри reference-пикера – тоже пропускаем
      if (e.target.closest('.cbs-dropdown') || e.target.closest('.cbs-dropdown-menu')) {
        return
      }

      const inside = this.$el.contains(e.target)
      const currentRx = this.selectedCell?.rx ?? null
      const clickedRx = this._closestRowRx?.(e.target)

      // внутри таблицы, клик по другой строке – onCellClick сам всё обработает
      if (inside && clickedRx != null && currentRx != null && clickedRx !== currentRx) return

      // клик вне строк таблицы – если есть последняя выделенная строка, пытаемся её сохранить
      if (clickedRx == null && this._lastSelectedRx != null) {
        // Force blur on active editable element so its value is committed before save
        const active = document.activeElement
        if (active && active.getAttribute('contenteditable') === 'true') {
          active.blur()
        }
        this.$nextTick(() => {
          this.commitRowIfNeeded?.(this._lastSelectedRx)
          this._lastSelectedRx = null
          this.selectedCell = null
          this.updateShowPlusButton?.({})
          this.updateShowDeleteButton?.({})
          this.updateShowCopyButton?.({})
        })
      }
    },
    _closestRowRx(el) {
      const tr = el?.closest?.('tr[data-rx]')
      if (!tr) return null
      const n = Number(tr.getAttribute('data-rx'))
      return Number.isFinite(n) ? n : null
    },
    ensureDirtySet() {
      if (!(this._dirtyRows instanceof Set)) this._dirtyRows = new Set()
      return this._dirtyRows
    },
    rowKey(row, fallback) {
      return row?.id ?? row?.record?.id ?? row?.rx ?? fallback ?? null
    },
    async onPlusClick(row /* может быть null */) {
      // если row == null => добавление с корня
      const startRow = row || null

      // last-level => как раньше
      if (startRow && startRow.level === this.maxRowLevel()) {
        return this.newLineLegacy(startRow)
      }

      if (startRow && startRow.isFolder && startRow.level === this.maxRowLevel()) {
        return this.newLineLegacy(startRow)
      }

      return this.addToLastLevel(startRow)
    },

  },
  async addToLastLevel(startRow) {
    let current = startRow

    if (!current) {
      current = this.createEmptyLevelRow(1, null)
      this.insertRowToTree(current, null)
    }

    while (current.level < this.maxRowLevel()) {
      await this.ensureExpandedWithSubs(current)

      const nextLevel = current.isFolder ? current.level : current.level + 1

      if (nextLevel === this.maxRowLevel()) {
        const leafParent = await this.ensureLeafParentRow(current, nextLevel)
        return this.newLineLegacy(leafParent)
      }

      const nextRow = await this.ensureIntermediateRow(current, nextLevel)

      this.fillGroupByFieldsFromParent(nextRow, current)

      current = nextRow
    }
  },
  async ensureExpandedWithSubs(row) {
    if (!row.isExpanded) this.onClickRowExpand(row, 'row')

    const cfg = this.getCfgForSubrows('row', row)
    if (!cfg) {
      row.subStatus = 'empty'
      row.subs = row.subs || []
      return
    }

    if (row.subStatus === 'empty') {
      await this.initSubRows(row, cfg)
      if (!row.subs || row.subs.length === 0) row.subStatus = 'empty'
    }
  },
  async ensureIntermediateRow(parent, level) {
    const existing = (parent.subs || []).find(r => r.level === level)
    if (existing) return existing

    const row = this.createEmptyLevelRow(level, parent)
    parent.subs = parent.subs || []
    parent.subs.unshift(row)
    this.recalculateZIndices?.()
    return row
  },
  async ensureLeafParentRow(parent, leafLevel) {
    const existing = (parent.subs || []).find(r => r.level === leafLevel)
    if (existing) return existing

    const leafParent = this.createEmptyLevelRow(leafLevel, parent)
    parent.subs = parent.subs || []
    parent.subs.unshift(leafParent)
    this.recalculateZIndices?.()
    return leafParent
  },
  createEmptyLevelRow(level, parentRow) {
    const cfg = this.formParam.rows[level - 1]
    if (!cfg) throw new Error(`No cfg for level=${level}`)

    const rec = this.getNewEditableRecord(cfg.objectid, parentRow) // наследует атрибуты
    const cells = this.getCellsForEditableRecord(cfg, rec)

    return {
      level,
      level_with_folders: parentRow ? parentRow.level_with_folders + 1 : 1,
      isExpanded: false,
      record: rec,
      cells,
      subs: [],
      status: 'new',
      subStatus: 'empty',
      isFolder: false,
      parentRow: parentRow || null,
      cfg,
      addrecord: cfg.addrecord || false,
      mode: cfg.mode || null,
      zIndex: 1,
    }
  },
  fillGroupByFieldsFromParent(child, parent) {
    const { cfg } = parent
    const pairs = cfg?.results || (cfg?.result ? [cfg.result] : [])
    pairs.forEach(p => {
      const sid = this.entities?.[p.destination_entityid]?.entity?.sid
      if (!sid) return
      if (parent.record?.[sid] && child.record?.[sid]) {
        child.record[sid] = { ...parent.record[sid] }
      }
    })
  },
}
</script>

<style scoped>

.pivot-wrapper {
  max-height: 75vh;
  min-height: 25vh;
  overflow: auto;
  position: relative;
}

.pivot-table thead {
  position: sticky;
  top: 0;
  background: white;
  z-index: 100;
}

.pivot-table thead th {
  background-clip: padding-box;
  border: 1px solid #dee2e6;
  background-color: white;
}

.pivot-table th:first-child,
.pivot-table td:first-child {
  position: sticky;
  left: 0;
  z-index: 95;
  background: white;
  background-clip: padding-box;
  min-width: 40px;
}

.pivot-table th:nth-child(2),
.pivot-table td:nth-child(2) {
  position: sticky;
  left: 40px;
  z-index: 94;
  background-color: white;
  background-clip: padding-box;
  min-width: 100px;
}

.pivot-table thead th:first-child,
.pivot-table thead th:nth-child(2) {
  z-index: 101;
}

.pivot-table tr.row-selected > td,
.pivot-table tr.row-selected > th {
  background-color: #f0f6ff;
}

.pivot-table tr.row-selected > td:first-child::before {
  content: "";
  position: absolute;
  left: 0; top: 0; bottom: 0;
  width: 3px;
  background: #1a73e8; /* same blue */
  opacity: 0.6;
}
.cell-saving {
  color: #999;
  cursor: wait;
}

.cell-selected {
  outline: 3px solid #1a73e8 !important;
  outline-offset: -3px !important;
}

.cell-selected * {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.cell-selected ::v-deep(.cell-edit) {
  border: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
}

.cell-selected ::v-deep(.cell-edit:focus) {
  border: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
}

/* Base floating button styles */
.floating-btn {
  position: fixed;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  z-index: 1000;
  pointer-events: auto;
  cursor: pointer;
  transition: top 0.15s ease, left 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
}

.floating-btn:hover {
  transform: scale(1.15);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.floating-btn:active {
  transform: scale(1.05);
}

.plus-button,
.floating-plus-button {
  background: #007bff;
}

.floating-plus-button:hover {
  background: #0056b3;
}

.floating-delete-button {
  background: #dc3545;
}

.floating-delete-button:hover {
  background: #c82333;
}

.floating-copy-button {
  background: #17a2b8;
}

.floating-copy-button:hover {
  background: #138496;
}

.pivot-dirty-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 4px 0;
  color: #ff9f43;
}
tr.row-dirty > td:first-child {
  box-shadow: inset 3px 0 0 #ff9f43;
}
.row-new {
  background-color: #f0f7ff !important;
  transition: background-color 0.15s ease;
}

.row-new:hover {
  background-color: #e6f7ff !important;
}

/* Row hover effect - only for non-sticky cells */
.pivot-table tbody tr:hover > td:not(.sticky-col-first):not(.sticky-col-second) {
  background-color: #fafafa;
}

.cell-yellow {
  background-color: lightyellow !important;
}

.cell-ref {
  background-color: #E8F2FF !important;
}

.cell-required-empty {
  background-color: #fde2e2 !important;
  box-shadow: inset 0 0 0 1px #e57373;
}

.pivot-table td .editor { pointer-events: none; }
.pivot-table td.cell-selected .editor { pointer-events: auto; }

/* Inline plus button (in row-new) */
.row-new .plus-button {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #007bff;
  color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* Loading indicator animation */
.text-secondary {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}

/* Smooth transitions for cells */
.pivot-table td {
  transition: background-color 0.1s ease;
}

</style>
